/*
 * RUG FINDER — the storefront client. One upload, everything else automatic.
 *
 * THE PHOTO NEVER LEAVES THE DEVICE EXCEPT AS ONE ANALYSIS REQUEST, and it is
 * never stored anywhere. A downscaled copy goes to the analyser Worker, which
 * holds it in memory and forgets it; the FULL-RESOLUTION original stays in this
 * page, is sampled here for colour, is used here as the preview backdrop, and
 * is dropped when the tab closes.
 *
 * WHAT IS MEASURED LOCALLY, FOR NOTHING:
 *   THE FLOOR'S LIGHTNESS.  The Worker returns where the floor IS and what it
 *     is MADE OF - both readable from a photograph. The lightness is measured
 *     here from those pixels, anchored on a white surface when the Worker found
 *     one. Doing it locally is not a cost saving: it is the only way to get the
 *     full-resolution pixels rather than the downscale the model saw.
 *   THE RANKING.  ~800 candidates in well under a millisecond, so changing the
 *     size re-ranks an already-visible grid instead of asking anyone anything.
 *
 * EXPOSURE IS THE REASON THE FLOOR READ IS A POSTERIOR AND NOT A NUMBER. A
 * +/-1 EV swing moves measured L* by ~32 units against a decision band 17 units
 * wide, so a single reading cannot be trusted. What IS trustworthy is the
 * material - hue and texture survive exposure - and a white anchor, which
 * cancels it. The engine is handed a distribution and ranks on its 10th
 * percentile.
 */
(function () {
  'use strict';

  var CFG = window.RUG_FINDER_CONFIG || {};
  var INDEX_POINTER = CFG.pointer;          /* .../fit/current.json */
  var ANALYSER = CFG.analyser;              /* the Worker URL */
  var VISUALISER = CFG.visualiser;          /* the RENDER Worker, or blank */
  var VIS_SITEKEY = CFG.sitekey || '';
  var MAX_EDGE = 1024;                      /* what the model is sent */
  var JPEG_Q = 0.82;

  var idx = null, photo = null, read = null, band = null, results = null;
  /* The room's own score, computed once per photograph. `null` until an
   * analysis lands, and `{scored:false}` for a space we deliberately do not
   * score - see `roomScore`. */
  var roomRead = null;
  /* HOW MANY RUGS THE PAGE RETURNS. Ten, by direct decision of the user.
   * It is a ceiling and not a promise: `out.short` says when fewer genuinely
   * fit, and the page says so rather than padding the grid with rugs the
   * engine has already judged wrong for the room. */
  var RESULTS = 10;
  /* ── A SECOND TEN TO SWAP TO (2026-09-07) ─────────────────────────────────
   * The countdown on screen 4 replaces the ten when it reaches zero, so the
   * ranker is asked for twice as many and the page shows a window of ten.
   *
   * IT COSTS NOTHING. `rank()` scores every candidate whatever `top` is - the
   * argument only decides where the list is cut - so this is the same work
   * with a longer slice. No second room read, no second analyser call, no
   * second anything.
   *
   * THE SECOND TEN ARE RANKS 11-20 OF THE SAME RANKING: the same room, the
   * same scoring, the same vetoes, the next ten best. They are not a different
   * kind of recommendation and they are not worse ones in any sense the engine
   * can see - `CLOSE_SPREAD` records that the ten cards' own spread is a
   * median 0.86 points, so rank 11 is not meaningfully behind rank 10. */
  var RESULTS_POOL = 20;

  /* THE FEWEST A LAYOUT MUST BE ABLE TO FILL before it is worth offering.
   * `match.MIN_VIABLE`, for the reason recorded there: below this the page is a
   * near-empty grid, which reads as "we have nothing" even when another LAYOUT
   * of the same room has plenty. */
  var MIN_VIABLE = 6;

  /* `placement` IS STILL HERE AND IS NO LONGER A CONTROL. It is set once from
   * `idx.placements[room][0]` - `placement.py` returns them "most-recommended
   * first", each with its own sources - and nothing on the page changes it.
   *
   * THE CHIPS AND THE SOFA SLIDER WERE REMOVED ON 2026-09-06. A customer sees
   * the answer, not the settings that produced it. Nothing about the FIRST
   * result moved: the default was already `placements[0]`, so the removal takes
   * away an override rather than a recommendation - and it makes this surface
   * agree with `rug-visualiser.js`, whose `recommendation()` has always read
   * `placements[0]` with no controls at all. Before this, a size changed here
   * and a size named in the product-page popup could disagree. */
  var state = { room: null, placement: null, sizeKey: null };

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var el = function (t, c, x) {
    var n = document.createElement(t);
    if (c) n.className = c;
    if (x !== undefined) n.textContent = x;
    return n;
  };

  /* ── THE FLOW — FOUR SCREENS, AND THE BACK BUTTON WORKS ─────────────────
   * By direct decision of the user (2026-09-07): one screen at a time, with a
   * forward button between each, so the customer is LED rather than presented
   * with a page.
   *
   * IT WAS FIVE. The photography rules were a screen of their own and the
   * upload was another; the rules are gone and the upload moved up, because
   * that was "too much reading before the one action the page exists for".
   *
   * MOVEMENT PUSHES A HISTORY ENTRY. Without it the browser back button leaves
   * the page - and on screens 4 and 5 that throws away a PAID analysis, which
   * the customer has no way of knowing and no way of getting back except by
   * spending another one. So back steps through the flow, and only leaves from
   * screen 1.
   *
   * A RELOAD ALWAYS LANDS ON SCREEN 1, whatever the URL says. The hash is
   * written for the back button, not as an address: screens 4 and 5 exist only
   * while an analysis is in memory, and a bookmarked `#step-5` would otherwise
   * open an empty results grid. `replaceState` on boot is what makes the first
   * entry in the history stack screen 1 rather than whatever was there.
   */
  var MAX_SCREEN = 4;
  var screen = 1;

  function panels() {
    return root ? root.querySelectorAll('[data-panel]') : [];
  }

  /* THE OTHER EIGHT SECTIONS ARE FOUND STRUCTURALLY, NOT BY NAME. They are
   * siblings of this section inside `<main>`, and their ids carry the theme's
   * own template id (`shopify-section-template--16632806473861__benefits`),
   * which changes per theme. Walking `nextElementSibling` from our own wrapper
   * costs nothing and cannot go stale when a section is renamed or reordered.
   *
   * THEY ARE HIDDEN ON EVERY SCREEN NOW, INCLUDING THE FIRST (2026-09-07), by
   * direct decision: nothing under the fold on screen 1. They are HIDDEN and
   * not removed, and they still render on every other page that uses them. */
  var siblings = null;
  function laterSections() {
    if (siblings) return siblings;
    siblings = [];
    var own = root && root.closest ? root.closest('.shopify-section') : null;
    for (var n = own && own.nextElementSibling; n; n = n.nextElementSibling) {
      if (n.classList && n.classList.contains('shopify-section')) siblings.push(n);
    }
    return siblings;
  }

  /* Screens 3 and 4 do not exist without an analysis. A hash, a stale history
   * entry or a fast double-press must not land on an empty results grid. */
  function reachable(n) {
    if (n >= 3 && !results) return 2;
    return Math.max(1, Math.min(MAX_SCREEN, n));
  }

  function go(n, push) {
    if (!root) return;
    n = reachable(n);
    screen = n;
    root.setAttribute('data-screen', String(n));
    var list = panels();
    for (var i = 0; i < list.length; i++) {
      list[i].hidden = list[i].getAttribute('data-panel') !== String(n);
    }
    /* NOTHING ELSE ON ANY SCREEN. They are HIDDEN rather than removed, and
     * hiding them took §4's wording off this page - so screen 4 prints the two
     * lines itself, whole, beneath the ten cards. That is what licenses the
     * short risk line on each of them. */
    var later = laterSections();
    for (i = 0; i < later.length; i++) later[i].hidden = true;
    /* THE SITE HEADER COMES BACK FROM SCREEN 2. `rf-lead` is written on <body>
     * by Liquid so the header is never painted on the first screen; dropping it
     * here is what returns the nav, the logo and the cart, and putting it back
     * is what takes them away again if the customer steps back. The FOOTER's
     * class (`rf-flow`) is never touched - it is hidden for the whole flow. */
    try { document.body.classList.toggle('rf-lead', n === 1); } catch (e) {}

    if (push !== false) {
      try {
        history.pushState({ rugScreen: n }, '', '#step-' + n);
      } catch (e) { /* a history that refuses is not a reason to stop */ }
    }
    /* ── THE STICKY HEADER IS MEASURED, NOT ASSUMED ────────────────────────
     * Scrolling to the finder's own top put every screen's HEADING underneath
     * the header: measured on the live page at 390, the header is 60px tall and
     * pinned at top -1, and screen 4's h2 landed at top 0 — completely covered.
     * Found by looking at the rendered page; no source assertion could see it.
     *
     * IT IS MEASURED RATHER THAN READ FROM A THEME TOKEN. Horizon declares
     * `--header-height` and it resolves EMPTY on the document element, so a
     * `var(--header-height, 0px)` would silently be 0 and this would still be
     * broken. The element's own height is the thing that is actually covering
     * the content.
     *
     * ONLY A PINNED STICKY OR FIXED HEADER COUNTS. A header scrolled out of
     * view covers nothing, so allowing for it would leave a gap instead. */
    var y = root.getBoundingClientRect().top + window.pageYOffset
            - stickyOffset() - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
    /* Focus the new screen so a keyboard or screen-reader user is moved with
     * everyone else rather than left at the bottom of the screen they were on.
     * `-1` because a section is not naturally focusable. */
    var live = root.querySelector('[data-panel="' + n + '"]');
    if (live) {
      var h = live.querySelector('h1, h2') || live;
      h.setAttribute('tabindex', '-1');
      try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
    }
  }

  /* How much of the top of the viewport is covered by something pinned there. */
  function stickyOffset() {
    var most = 0;
    var cands = document.querySelectorAll(
      '.shopify-section-group-header-group, header, .header-section');
    for (var i = 0; i < cands.length; i++) {
      var e = cands[i], cs;
      try { cs = window.getComputedStyle(e); } catch (err) { continue; }
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
      var r = e.getBoundingClientRect();
      /* PINNED, not merely present. A sticky header that has been scrolled past
       * sits above the viewport and covers nothing. */
      if (r.top > 4 || r.bottom <= 0) continue;
      if (r.bottom > most) most = r.bottom;
    }
    return most;
  }

  /* ── ONE AUTO-ADVANCE, USED BY BOTH RAILS ────────────────────────────────
   * Extracted from `bindReviews` on 2026-09-07 when the example pair was asked
   * to move on its own too. The reviews carousel already had every property
   * this needs and the choice was to copy it or to share it - and a second
   * copy is how the two would come to disagree about what "pause on hover"
   * means. Same rule the drag module, the modal shell and the gift row already
   * follow in this theme.
   *
   * IT RETURNS null UNDER REDUCED MOTION, and the caller does nothing further.
   * The timer is NEVER CREATED rather than created and suppressed: there is
   * then nothing to leak, nothing to resume by accident, and no state in which
   * a stray `play()` starts movement the customer asked not to see.
   *
   * A MANUAL SWIPE RESETS THE CLOCK INSTEAD OF FIGHTING IT, and that needs the
   * rail's own scrolls told apart from the customer's. Without the `driving`
   * flag the timer's own smooth-scroll fires the scroll handler, which bumps
   * the timer, which pushes the next tick out past the interval on every
   * cycle - the rail would still advance and would drift a little further
   * behind every time. `driving` is set for the scroll the timer causes and
   * cleared when it settles.
   *
   * PAUSE IS ON `pointerenter` AND `focusin`. Hover alone is not enough: a
   * keyboard user tabbing into a slide has no pointer, and the rail sliding
   * out from under the element they just focused is the failure the pause
   * exists to prevent. `document.hidden` stops it too - advancing in a
   * background tab is work nobody sees. */
  function autoAdvance(wrap, rail, opts) {
    var reduce = false;
    try {
      reduce = window.matchMedia &&
               window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { /* an old browser: treat it as motion allowed */ }
    if (reduce) return null;

    var ms = opts.ms, tick = null, driving = false, settle = null;

    function play() {
      if (tick) return;
      tick = setInterval(function () {
        driving = true;
        opts.next();
        clearTimeout(settle);
        settle = setTimeout(function () { driving = false; }, 700);
      }, ms);
    }
    function stop() { if (tick) { clearInterval(tick); tick = null; } }
    /* A PRESS OR A SWIPE RESTARTS THE CLOCK RATHER THAN LEAVING IT STOPPED,
     * so somebody who taps an arrow to look at one slide is not left with a
     * dead rail.
     *
     * IT IS A RESET, NOT A GRACE PERIOD, AND THAT IS A CORRECTION TO WHAT THE
     * REVIEWS RAIL HAS ALWAYS DONE. This was `stop(); setTimeout(play, ms)`,
     * which waits one whole interval and THEN starts an interval - so the next
     * advance came 6.15 s after a swipe rather than 3 s, measured on the live
     * page. Two intervals is a defensible grace period and it is not what
     * "resets the timer" means, so the clock now restarts from the gesture.
     * The reviews inherit the fix, which is the point of there being one
     * implementation. */
    function bump() { stop(); play(); }

    (opts.controls || []).forEach(function (node) {
      if (node) node.addEventListener('click', bump);
    });
    rail.addEventListener('scroll', function () {
      if (driving) return;               /* our own smooth-scroll, not theirs */
      bump();
    });
    wrap.addEventListener('pointerenter', stop);
    wrap.addEventListener('pointerleave', play);
    wrap.addEventListener('focusin', stop);
    wrap.addEventListener('focusout', play);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else play();
    });
    play();
    return { play: play, stop: stop, bump: bump };
  }

  /* ── THE TWO EXAMPLES: A SWIPEABLE PAIR, BAD FIRST ───────────────────────
   * By direct decision of the user. The rail is a scroll-snap container; TOUCH
   * scrolls it natively with the platform's own momentum and snap, and the
   * theme's own `[data-rz-drag]` module - already used by the product rail and
   * the reviews - adds the one thing a mouse lacks, which is grab-and-pull.
   * There is no second swiper implementation in this file.
   *
   * THE ARROWS AND DOTS ARE BUILT HERE RATHER THAN IN LIQUID so their count
   * follows the rail. Nothing on a still screen says a pair moves, and a
   * keyboard has no swipe at all - without them the good example is reachable
   * only by a gesture a desktop visitor has no reason to try.
   *
   * THE DOT STATE COMES FROM `scrollLeft`, NOT FROM THE LAST BUTTON PRESSED. A
   * customer who swipes past a button they never touched must still see the
   * right dot lit, and the scroll position is the only thing that knows.
   */
  function bindSwipe() {
    var wrap = $('[data-rf-swipe]', root);
    if (!wrap) return;
    var rail = wrap.querySelector('.rf-swipe__rail');
    var nav = wrap.querySelector('[data-rf-swipe-nav]');
    if (!rail || !nav) return;
    var slides = rail.querySelectorAll('.rf-swipe__slide');
    if (slides.length < 2) { nav.hidden = true; return; }

    var prev = el('button', 'rf-swipe__arrow rf-swipe__arrow--prev', '\u2039');
    var next = el('button', 'rf-swipe__arrow rf-swipe__arrow--next', '\u203A');
    prev.type = next.type = 'button';
    prev.setAttribute('aria-label', 'Previous example');
    next.setAttribute('aria-label', 'Next example');
    var dots = el('span', 'rf-swipe__dots');
    var dot = [];
    for (var i = 0; i < slides.length; i++) {
      var d = el('button', 'rf-swipe__dot');
      d.type = 'button';
      d.setAttribute('aria-label', 'Example ' + (i + 1) + ' of ' + slides.length);
      (function (n) {
        d.addEventListener('click', function () { to(n); });
      })(i);
      dots.appendChild(d);
      dot.push(d);
    }
    nav.appendChild(prev); nav.appendChild(dots); nav.appendChild(next);

    function at() {
      var w = rail.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / w)));
    }
    function paint() {
      var i = at();
      for (var k = 0; k < dot.length; k++) {
        dot[k].classList.toggle('is-on', k === i);
        dot[k].setAttribute('aria-current', k === i ? 'true' : 'false');
      }
    }
    /* IT LOOPS NOW, SO THE ARROWS NO LONGER DISABLE (2026-09-07). They used to
     * go dead at the first and last slide, and this file said so in words: "the
     * example pair's arrows do - that rail has a first and a last and says so".
     * That was true of a rail that stopped at the ends. It advances on its own
     * past the last slide back to the first, so a button reading "you cannot go
     * further" beside a rail that goes further by itself contradicts what the
     * customer is watching. Bad -> good -> bad, and the arrows agree. */
    function to(i) {
      var n = slides.length;
      i = ((i % n) + n) % n;
      rail.scrollTo({ left: i * rail.clientWidth, behavior: 'smooth' });
    }
    prev.addEventListener('click', function () { to(at() - 1); });
    next.addEventListener('click', function () { to(at() + 1); });
    var t2 = null;
    rail.addEventListener('scroll', function () {
      if (t2) return;
      t2 = setTimeout(function () { t2 = null; paint(); }, 90);
    });
    window.addEventListener('resize', paint);
    paint();

    /* THE SAME TIMER THE REVIEWS RUN ON, three seconds, looping. Everything
     * above works with it or without it - under reduced motion `autoAdvance`
     * returns null and the pair stays fully reachable by swipe, drag, arrow
     * and dot. What is removed is the movement, not the content. */
    autoAdvance(wrap, rail, {
      ms: EXAMPLE_MS,
      next: function () { to(at() + 1); },
      controls: [prev, next, dots]
    });
  }

  /* ── THE REVIEWS ON SCREEN 1: A RAIL THAT MOVES ON ITS OWN ───────────────
   * By direct decision of the user (2026-09-07): three reviews under the CTA,
   * sliding every three seconds, looping, pausing on hover and focus, and
   * nothing at all under `prefers-reduced-motion`.
   *
   * IT IS THE EXAMPLE PAIR'S RAIL WITH A TIMER ON IT. Same scroll-snap
   * container, same `[data-rz-drag]`, same dots-from-scrollLeft rule - a
   * second carousel implementation in this file is what the swipe block
   * already refuses, and this is the third surface in the theme to use the one
   * drag module rather than the third to invent one.
   *
   * THE TIMER IS NEVER CREATED UNDER REDUCED MOTION, rather than created and
   * then suppressed. There is then nothing to leak, nothing to resume by
   * accident, and no state in which a stray `play()` starts an animation the
   * customer has asked not to see. The three reviews stay fully reachable by
   * swipe, drag, arrow and dot - what is removed is the movement, not the
   * content.
   *
   * PAUSE IS ON `pointerenter` AND `focusin`, RESUME ON THEIR OPPOSITES.
   * Hover alone is not enough: a keyboard user tabbing into a quote has no
   * pointer, and having the rail slide out from under the element they just
   * focused is the exact failure the pause exists to prevent. `document
   * .hidden` stops it too - advancing through three reviews in a background
   * tab is work nobody sees and a wake-up nobody asked for. */
  var REVIEW_MS = 3000;
  /* THE EXAMPLES ADVANCE ON THE SAME BEAT AS THE REVIEWS, and it is a separate
   * constant because they are separate decisions about separate rails - one
   * number read by two surfaces is how a change to one silently moves the
   * other. They happen to agree today. */
  var EXAMPLE_MS = 3000;

  function bindReviews() {
    var wrap = $('[data-rf-reviews]', root);
    if (!wrap) return;
    var rail = wrap.querySelector('.rf-reviews__rail');
    var nav = wrap.querySelector('[data-rf-reviews-nav]');
    if (!rail || !nav) return;
    var slides = rail.querySelectorAll('.rf-reviews__slide');
    /* ONE REVIEW IS NOT A CAROUSEL. It renders, and nothing moves or is
     * offered to be moved - a dot row with one dot and two dead arrows is
     * furniture pretending to be a control. */
    if (slides.length < 2) { nav.hidden = true; return; }

    var prev = el('button', 'rf-reviews__arrow rf-reviews__arrow--prev', '\u2039');
    var next = el('button', 'rf-reviews__arrow rf-reviews__arrow--next', '\u203A');
    prev.type = next.type = 'button';
    prev.setAttribute('aria-label', 'Previous review');
    next.setAttribute('aria-label', 'Next review');
    var dots = el('span', 'rf-reviews__dots');
    var dot = [];
    for (var i = 0; i < slides.length; i++) {
      var d = el('button', 'rf-reviews__dot');
      d.type = 'button';
      d.setAttribute('aria-label', 'Review ' + (i + 1) + ' of ' + slides.length);
      (function (n) { d.addEventListener('click', function () { to(n); }); })(i);
      dots.appendChild(d);
      dot.push(d);
    }
    nav.appendChild(prev); nav.appendChild(dots); nav.appendChild(next);

    function at() {
      var w = rail.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(rail.scrollLeft / w)));
    }
    function paint() {
      var i = at();
      for (var k = 0; k < dot.length; k++) {
        dot[k].classList.toggle('is-on', k === i);
        dot[k].setAttribute('aria-current', k === i ? 'true' : 'false');
      }
    }
    /* IT LOOPS, SO THE ARROWS NEVER DISABLE. The example pair's arrows do -
     * that rail has a first and a last and says so. This one has neither. */
    function to(i) {
      var n = slides.length;
      i = ((i % n) + n) % n;
      rail.scrollTo({ left: i * rail.clientWidth, behavior: 'smooth' });
    }
    /* THE `stop()` THAT USED TO BE HERE IS GONE WITH THE LOCAL TIMER. It was
     * calling a function that no longer exists in this scope - a ReferenceError
     * on every arrow and every dot, and the rail would have stopped responding
     * to its own controls. `autoAdvance` binds `bump` to the same three
     * elements, and bump's own `stop()` does what these did. */
    prev.addEventListener('click', function () { to(at() - 1); });
    next.addEventListener('click', function () { to(at() + 1); });

    var t = null;
    rail.addEventListener('scroll', function () {
      if (t) return;
      t = setTimeout(function () { t = null; paint(); }, 90);
    });
    window.addEventListener('resize', paint);
    paint();

    /* THE TIMER, NOW THE SHARED ONE. Everything above works with it or without
     * it. This block used to be sixty lines of its own and was lifted into
     * `autoAdvance` on 2026-09-07 when the example pair needed the same
     * behaviour - copying it would have left two definitions of "pause on
     * hover" free to drift apart. The reviews gained one thing in the move: a
     * manual swipe now resets the clock, which it did not before. */
    autoAdvance(wrap, rail, {
      ms: REVIEW_MS,
      next: function () { to(at() + 1); },
      controls: [prev, next, dots]
    });
  }

  function bindFlow() {
    root.addEventListener('click', function (ev) {
      var next = ev.target.closest ? ev.target.closest('[data-rf-next]') : null;
      if (next) { go(Number(next.getAttribute('data-rf-next')), true); return; }
    });
    window.addEventListener('popstate', function (e) {
      var n = (e.state && e.state.rugScreen) || 1;
      go(n, false);
    });
    try { history.replaceState({ rugScreen: 1 }, '', location.pathname + location.search); }
    catch (e) { /* nothing depends on it beyond the first back press */ }
  }

  /* ── the index ───────────────────────────────────────────────────────────
   * Two fetches: a tiny pointer with a ROTATING QUERY STRING, then the
   * content-addressed index it names. The query string is what actually
   * produces a MISS on this CDN - a cache-control header alone did not, which
   * is recorded from measurement rather than assumed. */
  function loadIndex() {
    var bust = 'v=' + Math.floor(Date.now() / 60000);
    return fetch(INDEX_POINTER + (INDEX_POINTER.indexOf('?') < 0 ? '?' : '&') + bust)
      .then(function (r) {
        if (!r.ok) throw new Error('pointer ' + r.status);
        return r.json();
      })
      .then(function (p) {
        var base = INDEX_POINTER.replace(/\/fit\/current\.json.*$/, '/');
        /* THE INDEX SHA, which nothing assigned until now - so `_fit_index`
         * reached every order as "<version>:" with the half that identifies
         * WHICH catalogue produced the recommendation simply missing, and the
         * replay-the-tuple guarantee in recordChoice was unfulfillable. The
         * pointer names the content-addressed object, so its sha is right
         * here. */
        window.__FIT_SHA = String(p.sha || p.index || '').replace(/^.*index-|\.json$/g, '');
        return fetch(base + p.index).then(function (r) {
          if (!r.ok) throw new Error('index ' + r.status);
          return r.json();
        });
      });
  }

  /* ── reading the photo ───────────────────────────────────────────────── */
  function loadPhoto(file) {
    return new Promise(function (res, rej) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        /* The full-resolution canvas stays here. It is what the colour is
         * measured from and what the preview draws on; the model gets a
         * downscale. Measuring the downscale would throw away the resolution
         * that makes a small floor patch a reliable sample. */
        var full = document.createElement('canvas');
        full.width = img.naturalWidth; full.height = img.naturalHeight;
        full.getContext('2d').drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        res({ canvas: full, w: img.naturalWidth, h: img.naturalHeight, url: null });
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        rej(new Error('That file could not be opened as an image.'));
      };
      img.src = url;
    });
  }

  function downscaleToBase64(p) {
    var scale = Math.min(1, MAX_EDGE / Math.max(p.w, p.h));
    var c = document.createElement('canvas');
    c.width = Math.round(p.w * scale); c.height = Math.round(p.h * scale);
    c.getContext('2d').drawImage(p.canvas, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', JPEG_Q).split(',')[1];
  }

  /* ── colour, measured here ───────────────────────────────────────────────
   * The same CIELAB the catalogue's own palettes were measured in. Written out
   * rather than approximated: mixing this scale with any other silently halves
   * every threshold the engine uses. */
  function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  function rgbToLab(r, g, b) {
    var R = srgbToLinear(r / 255), G = srgbToLinear(g / 255), B = srgbToLinear(b / 255);
    var x = (0.4124564 * R + 0.3575761 * G + 0.1804375 * B) / 0.95047;
    var y = (0.2126729 * R + 0.7151522 * G + 0.0721750 * B) / 1.0;
    var z = (0.0193339 * R + 0.1191920 * G + 0.9503041 * B) / 1.08883;
    var e = 216 / 24389, k = 24389 / 27;
    var f = function (t) { return t > e ? Math.cbrt(t) : (k * t + 16) / 116; };
    var fx = f(x), fy = f(y), fz = f(z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function sampleRegion(p, rect) {
    if (!rect) return null;
    var x0 = Math.max(0, Math.round(rect[0] * p.w));
    var y0 = Math.max(0, Math.round(rect[1] * p.h));
    var x1 = Math.min(p.w, Math.round(rect[2] * p.w));
    var y1 = Math.min(p.h, Math.round(rect[3] * p.h));
    if (x1 - x0 < 4 || y1 - y0 < 4) return null;
    var d = p.canvas.getContext('2d').getImageData(x0, y0, x1 - x0, y1 - y0).data;
    var L = [], a = 0, b = 0, n = 0;
    for (var i = 0; i < d.length; i += 4) {
      var lab = rgbToLab(d[i], d[i + 1], d[i + 2]);
      L.push(lab[0]); a += lab[1]; b += lab[2]; n++;
    }
    if (!n) return null;
    /* THE MEDIAN, NOT THE MEAN. A floor patch picks up a specular highlight, a
     * shadow edge or a stray table leg, and a mean follows the outlier while a
     * median does not. */
    L.sort(function (x, y) { return x - y; });
    return { L: L[Math.floor(L.length / 2)], a: a / n, b: b / n, n: n };
  }

  /* THE ANCHOR IS WHAT CANCELS EXPOSURE, and it only works when the Worker
   * found a surface that is genuinely white PAINT. Measured on 119 real
   * interiors: one is present in about 65% of them, and where it is, the
   * residual spread is ~5 L*. Where it is not, the material prior carries the
   * estimate alone and the engine widens accordingly - which it does by itself,
   * because it is handed a posterior rather than a number. */
  var WHITE_PAINT_L = 92.3;      /* measured median across those 119 photos */

  function anchoredFloorL(p, r) {
    var floor = sampleRegion(p, r.floor_region);
    if (!floor) return { L: null, anchored: false };
    var white = sampleRegion(p, r.white_reference);
    if (!white || white.L < 60) return { L: floor.L, anchored: false };
    /* Scale the floor by however far the known-white surface was pushed from
     * where white should sit. First order, and first order is all an
     * uncalibrated JPEG supports. */
    var shift = WHITE_PAINT_L - white.L;
    return { L: Math.max(5, Math.min(98, floor.L + shift)), anchored: true,
             raw: floor.L, white: white.L };
  }

  /* ── THE ROOM'S OWN SCORE ────────────────────────────────────────────────
   * How finished the room looks AS IT IS, before any rug. Printed on screen 4
   * and again as the "before" half of every card's pair.
   *
   * ── THE CEILING IS 7 AND IT IS THE HONEST PART ─────────────────────────
   * A room without a rug is never finished, so there is always something a rug
   * can do. A score that could reach 10 would be claiming otherwise about a
   * room we are about to recommend ten rugs for.
   *
   * ── AND THE FLOOR OF THE SCALE IS 5, WHICH IS THE OTHER HALF OF THE SAME
   * DECISION. `CEILING - SWING` = 5.0, so a real room lands in 5.0-7.0 and the
   * thin-read value of 6.0 sits in the middle of it rather than at one end.
   * A pale-floored room is not a BAD room and must not read as 2/10; what it
   * is, is a room with more for a rug to do.
   *
   * ── THREE FAULTS THIS DESIGN EXISTS TO AVOID, EACH MEASURED ─────────────
   *   BLIND TO THE FLOOR.  A first version scored 5.6 on every floor, because
   *     the contrast term was computed against the floor itself. It is now two
   *     SEPARATE canvas reads - the floor rectangle, and the frame WITH THAT
   *     RECTANGLE EXCLUDED - so "separation" is genuinely floor-against-room.
   *   BACKWARDS ON PATTERN.  Plain scored 5.6 and busy 7.6, i.e. it rewarded a
   *     room for already being full. The curve PEAKS at 0.35 now: a room with a
   *     little pattern reads considered, none reads bare, and a lot reads busy.
   *   STYLE SWUNG 3.4 POINTS.  Removed as an input entirely, weight 0.00. It
   *     still writes the WORDS, where being wrong is embarrassing rather than
   *     measurable - 45 of the style matrix's 88 cells are tier C.
   *
   * ── IT IS NOT IN fit-engine.js, AND THAT IS DELIBERATE ──────────────────
   * That file is held byte-for-byte to `match.py` by `test_conformance`. This
   * has no Python counterpart and never will - it scores a ROOM, not a rug -
   * so putting it there would place a function with no mirror inside the file
   * whose entire purpose is mirroring.
   */
  var SCORE_CEILING = 7.0;
  var SCORE_SWING = 2.0;          /* so the band is 5.0 - 7.0 */
  var SCORE_THIN = 6.0;           /* a read we could not make; not a mark down */
  /* WEIGHTS. The floor moves the number more than anything else, which is the
   * right order: CLAUDE.md measures floor contrast as the largest single
   * ranking effect in the catalogue. At SWING 2.0 they are worth 1.10, 0.60 and
   * 0.30 points respectively. */
  var SCORE_W = { anchor: 0.55, separation: 0.30, pattern: 0.15, style: 0.00 };

  /* THE SECOND CANVAS READ. Downscaled first: a median and a spread do not need
   * 12 megapixels, and `getImageData` on a full-res canvas for the whole frame
   * is the one place this could cost a customer a visible pause.
   *
   * IT EXCLUDES THE FLOOR RECTANGLE. That exclusion IS the fix for "contrast
   * computed against itself" - without it the frame median contains the floor
   * and the separation term collapses toward zero on every photograph.
   *
   * `shift` IS THE SAME WHITE-REFERENCE CORRECTION `anchoredFloorL` APPLIES.
   * Comparing an anchored floor against an unanchored frame would be comparing
   * two different scales and calling the difference a measurement. */
  function sampleFrame(p, floorRect, shift) {
    var scale = Math.min(1, 160 / Math.max(p.w, p.h));
    var w = Math.max(1, Math.round(p.w * scale));
    var h = Math.max(1, Math.round(p.h * scale));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    c.getContext('2d').drawImage(p.canvas, 0, 0, w, h);
    var d;
    try { d = c.getContext('2d').getImageData(0, 0, w, h).data; }
    catch (e) { return null; }
    var fx0 = floorRect ? floorRect[0] * w : -1, fy0 = floorRect ? floorRect[1] * h : -1;
    var fx1 = floorRect ? floorRect[2] * w : -1, fy1 = floorRect ? floorRect[3] * h : -1;
    var L = [];
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (floorRect && x >= fx0 && x < fx1 && y >= fy0 && y < fy1) continue;
        var i = (y * w + x) * 4;
        L.push(rgbToLab(d[i], d[i + 1], d[i + 2])[0] + (shift || 0));
      }
    }
    if (L.length < 64) return null;
    L.sort(function (a, b) { return a - b; });
    var at = function (q) { return L[Math.min(L.length - 1, Math.floor(L.length * q))]; };
    /* THE SPREAD IS p10-p90, NOT min-max. One blown highlight or one black
     * doorway would otherwise set the whole reading. */
    return { L: at(0.5), spread: at(0.9) - at(0.1), n: L.length };
  }

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* HOW MUCH THE FLOOR ALREADY ANCHORS THE ROOM. Dark floors hold a room down;
   * a pale floor gives the eye nothing to stop at, which is why CLAUDE.md
   * records the pale-floor neutral room as the commonest room in this market
   * AND the one the catalogue answers worst. 20 and 85 are the ends of the
   * lightness ladder the fixtures span, not a fitted pair. */
  function anchorTerm(floorL) {
    if (floorL === null || floorL === undefined) return null;
    return clamp01((85 - floorL) / 65);
  }

  /* HOW FAR THE FLOOR SITS FROM THE REST OF THE ROOM. A floor and furnishings
   * on top of each other read as one surface. 40 L* is a full separation.
   *
   * KNOWN LIMIT, STATED: this and the frame's own tonal SPREAD correlate
   * strongly on the five photographs measured, so only ONE of them is a scored
   * term. Carrying both would double-count one observation. The spread is kept
   * for the reasons, where it describes rather than scores. */
  function separationTerm(floorL, frame) {
    if (floorL === null || floorL === undefined || !frame) return null;
    return clamp01(Math.abs(floorL - frame.L) / 40);
  }

  /* PEAKED AT 0.35. A room with a little pattern reads considered; none reads
   * bare and a lot reads busy, and BOTH ends are further from finished than the
   * middle. The first version had this backwards. */
  function patternTerm(load) {
    if (load === null || load === undefined) return null;
    return clamp01(1 - Math.abs(load - 0.35) / 0.5);
  }

  /* The same abstention rule `fit-engine.weighted` uses, and for the same
   * reason: a skipped input is never filled with a mid-range default, because
   * 0.5 is a claim and an abstention is the absence of one. */
  function weightedTerms(parts) {
    var tot = 0, acc = 0, n = 0;
    parts.forEach(function (pr) {
      if (pr[1] === null || pr[1] === undefined || !pr[0]) return;
      tot += pr[0]; acc += pr[0] * pr[1]; n++;
    });
    return n ? acc / tot : null;
  }

  function roomScore(p, r, floor, frame) {
    /* ── AN OUTDOOR SPACE IS NOT SCORED, AND THE RUGS STILL COME ───────────
     * "How finished does this room look" is not a question about a terrace.
     * The customer loses the SCORE, not the tool - the size band and the ten
     * rugs are untouched, because an outdoor seating group sizes correctly
     * through the living band.
     *
     * `unclear` IS TREATED AS INDOOR, deliberately and in the one direction
     * that is safe to be wrong in: a score on an ambiguous conservatory is a
     * number somebody can disagree with, where no score on an ordinary living
     * room is a feature silently missing. The Worker only ever returns
     * `outdoor` when it is sure. */
    if (r.setting === 'outdoor') {
      return { scored: false, why: 'outdoor', score: null, terms: null };
    }
    var terms = {
      anchor: anchorTerm(floor.L),
      separation: separationTerm(floor.L, frame),
      pattern: patternTerm(r.pattern_load)
    };
    /* THIN: we could not read the room well. It scores 6.0 - the middle of the
     * honest band - because a room should not be marked down for OUR failure to
     * see it. The floor is 55% of the weight, so without it what is left is
     * mostly guesswork whatever the other terms say. */
    var thin = r.confidence === 'low' || terms.anchor === null;
    if (thin) {
      return { scored: true, why: 'thin', score: SCORE_THIN, terms: terms,
               anchored: !!floor.anchored, frame: frame };
    }
    var w = weightedTerms([[SCORE_W.anchor, terms.anchor],
                           [SCORE_W.separation, terms.separation],
                           [SCORE_W.pattern, terms.pattern]]);
    if (w === null) {
      return { scored: true, why: 'thin', score: SCORE_THIN, terms: terms,
               anchored: !!floor.anchored, frame: frame };
    }
    var v = SCORE_CEILING - SCORE_SWING * (1 - w);
    return { scored: true, why: 'measured', terms: terms, frame: frame,
             anchored: !!floor.anchored,
             score: Math.round(v * 10) / 10,
             floorL: floor.L };
  }

  /* ── THE AFTER-NUMBER IS A RANK CURVE, NOT A MEASUREMENT OF FIT ──────────
   * By direct decision of the user (2026-09-07), against this module's own
   * standing objection, which is why it is written down here as well as in the
   * amendments.
   *
   * WHAT IT REPLACED. The "with this rug" number was the compatibility score
   * rescaled - `item.score * 10` - which is a real measurement of THIS rug
   * against THIS room. Measured across 48 rooms its spread over the ten cards
   * was a median 0.86 points and often 0.2, so the ten read as one number
   * printed ten times. The instruction was a dramatic, ordered lift: 9.8 at the
   * top and a systematic decline under it.
   *
   * SO THE NUMBER NOW CARRIES NO INFORMATION THE RANK POSITION DOES NOT
   * ALREADY CARRY. That is exactly the objection this file records against a
   * percentile - "it would say the same thing as the rank twice" - and the raw
   * fit score was chosen over one for that reason. The decision was taken with
   * that in view. DO NOT DESCRIBE THIS NUMBER AS A MEASUREMENT OF FIT
   * ANYWHERE, in copy or in a comment.
   *
   * WHAT IS STILL REAL IN IT: the BEFORE, which is measured, and the SPAN,
   * which is derived from the before. A room that started lower has further to
   * travel, so its ten spread wider - a 5.3 room's tenth card reads 8.1 and a
   * 6.2 room's reads 8.4. That is the anchor.
   *
   * THE STEPS VARY ON PURPOSE. "A uniform drop reads as a formula; the steps
   * should vary so the ten look like ten readings rather than an arithmetic
   * series." The nine fractions below are DESIGNED, not fitted to anything, and
   * that is stated rather than dressed up.
   *
   * THE FLOOR IS 8.0, at the palest room the scorer can produce; the best-lit
   * room floors at 8.7. Nothing we recommend ever prints below 8.0, because a
   * rug this system is recommending must not read as poor.
   *
   * MONOTONICITY IS BY CONSTRUCTION, NOT BY LUCK. Swept over every before from
   * 5.0 to 7.0 in 0.05 steps, one value at before 6.95 rounded EQUAL to the one
   * above it - two 9.1s on adjacent cards, which would read as a fault. A value
   * may never round equal to its predecessor, so it is pushed down 0.1. Zero
   * ties after it, across the whole band.
   *
   * WHEN `CLOSE_SPREAD` FIRES the note above the grid becomes load-bearing
   * rather than decorative: it already says the numbers are "a guide rather
   * than a verdict", and with a rank curve printing a clean decline over ten
   * rugs the engine cannot tell apart, that sentence is the only thing on the
   * page telling the truth about them. Do not remove it.
   */
  var AFTER_TOP = 9.8;
  var AFTER_SPAN_MIN = 1.1;      /* the best-lit room a score can produce */
  var AFTER_SPAN_MAX = 1.8;      /* the palest; 9.8 - 1.8 = the 8.0 floor  */
  var AFTER_STEPS = [0.06, 0.09, 0.17, 0.14, 0.13, 0.08, 0.15, 0.10, 0.08];

  function afterLadder(before, n) {
    var lo = SCORE_CEILING - SCORE_SWING;          /* 5.0 */
    var hi = SCORE_CEILING;                        /* 7.0 */
    var b = before === null || before === undefined ? lo
          : Math.max(lo, Math.min(hi, before));
    var span = AFTER_SPAN_MIN +
               (AFTER_SPAN_MAX - AFTER_SPAN_MIN) * (hi - b) / (hi - lo);
    var out = [AFTER_TOP], cum = 0;
    for (var i = 0; i < AFTER_STEPS.length; i++) {
      cum += AFTER_STEPS[i];
      out.push(AFTER_TOP - span * cum);
    }
    var seen = [];
    for (i = 0; i < out.length; i++) {
      var v = Math.round(out[i] * 10) / 10;
      if (i && v >= seen[i - 1] - 1e-9) v = Math.round((seen[i - 1] - 0.1) * 10) / 10;
      seen.push(v);
    }
    /* FEWER THAN TEN RUGS IS AN ANSWER THE ENGINE GIVES, and the ladder is
     * indexed by rank, so a short set simply uses its first N rungs. */
    return seen.slice(0, Math.max(1, n || seen.length));
  }

  /* ── WHY IT IS WHAT IT IS ────────────────────────────────────────────────
   * Two or three sentences, specific to their room, built from what was read.
   * An OBSERVATION about what the space needs - never a grading, and never a
   * sentence that would read the same for every room.
   *
   * THEY ARE LITERALS HERE, AND THAT IS NOT THE SAME DECISION AS THE CARD
   * REASONS. Those come from the INDEX because there are TWO engines writing
   * them and a second copy is how `match.py` and `fit-engine.js` came to carry
   * an em-dash and a hyphen for one sentence. Nothing in Python scores a room,
   * so there is no second copy to drift from.
   */
  /* ── THE ROOM AS A WHOLE, NEVER OBJECT BY OBJECT ─────────────────────────
   * By direct decision of the user (2026-09-07), replacing a paragraph that
   * read "Your light wood floor is pale" and "your 2-seat sofa".
   *
   * THE ARGUMENT IS ABOUT WHERE BEING WRONG SHOWS, and it is right. The floor
   * lightness is derived from two canvas reads anchored on a white reference
   * the model marks out, and the anchor is the least reliable thing in the
   * chain - a dark floor genuinely does come back light. Told "your floor is
   * pale" about a walnut floor, a customer knows we cannot see their room and
   * stops believing the ten rugs as well. Told "this room sits in one narrow
   * band of colour" about the same photograph, the sentence survives the same
   * error, because a dark floor under dark furniture is still one narrow band.
   *
   * SO EVERY SENTENCE HERE IS A STATEMENT ABOUT THE WHOLE FRAME. Style, how
   * the colours sit together, how much pattern there is, what the room feels
   * like. No object is named, no object is given a colour, and the floor
   * material - which used to lead - appears nowhere.
   *
   * WHAT IT IS BUILT FROM IS UNCHANGED and is still measured: `style` and
   * `colour_family` from the read, `pattern_load` from the read, and the
   * SEPARATION term from the two canvas reads. What changed is that these are
   * now reported as properties of the room rather than as facts about its
   * parts, which is what they always were - `separation` compares the floor
   * rectangle against the whole rest of the frame, so "the room sits in one
   * band" is a more literal description of that number than "your floor and
   * your furniture" ever was.
   *
   * WHERE THE READ IS THIN IT SAYS LESS, NOT MORE. "If the reading isn't
   * confident enough to say something general, say less rather than guessing
   * at a detail." A thin read returns two sentences about the photograph and
   * makes no claim about the room at all. */

  /* THE STYLE WORDS ARE THE ANALYSER'S OWN ELEVEN, mapped once. A style the
   * table does not carry is SKIPPED rather than printed raw - `boho` in a
   * sentence reads as a leak, and a style we cannot phrase is one we can do
   * without. */
  var STYLE_PHRASE = {
    'modern': 'put together in a modern way',
    'mid-century modern': 'put together in a mid-century way',
    'scandinavian': 'put together in a pale, Scandinavian way',
    'traditional': 'put together in a traditional way',
    'transitional': 'somewhere between traditional and modern',
    'boho': 'put together in a relaxed, layered way',
    'industrial': 'put together in a plain, industrial way',
    'farmhouse': 'put together in a soft, country way',
    'coastal': 'put together in a light, coastal way',
    'glam': 'put together in a polished, dressed way',
    'rustic': 'put together in a warm, rustic way'
  };

  /* HOW THE ROOM FEELS, FROM THE TWO NUMBERS THAT DECIDE IT. Pattern load and
   * tonal separation are the whole of what a photograph can honestly support
   * here; anything more would be a guess wearing an adjective. */
  function feelWord(load, sep) {
    var plain = typeof load === 'number' && load < 0.15;
    var busy = typeof load === 'number' && load > 0.6;
    var flat = sep !== null && sep !== undefined && sep < 0.35;
    if (busy) return 'full';
    if (plain && flat) return 'very calm and very quiet';
    if (plain) return 'calm and simple';
    if (flat) return 'quiet';
    return 'settled';
  }

  function scoreReasons(r, rs, spent) {
    var out = [];
    var used = spent || {};
    if (rs.why === 'thin') {
      out.push('This photo was hard to read, so this is a rough reading rather ' +
               'than a close one.');
      out.push('A wider, brighter shot would tell us more about the room.');
      return out;
    }

    var sep = rs.terms ? rs.terms.separation : null;
    var load = r.pattern_load;

    /* 1. STYLE AND FEEL, IN ONE SENTENCE. The style is the analyser's, the
     *    feel is derived from the two measured numbers. */
    var sty = STYLE_PHRASE[String(r.style || '').toLowerCase()];
    if (sty) {
      used.style = 1;
      out.push('This room is ' + sty + ', and it feels ' +
               feelWord(load, sep) + '.');
    } else {
      out.push('This room feels ' + feelWord(load, sep) + '.');
    }

    /* 2. HOW THE COLOURS SIT TOGETHER. `separation` is a whole-frame
     *    measurement - the floor rectangle against everything else - so this
     *    is the number described rather than an object given a colour. */
    if (sep !== null && sep !== undefined) {
      used.separation = 1;
      out.push(sep < 0.35
        ? 'Everything in it sits in one narrow band of colour, so nothing ' +
          'steps forward and nothing falls back.'
        : 'The tones in it are already spread apart, so the room has some ' +
          'depth to build on.');
    }
    /* 3. WHAT IS ON THE SURFACES. Whole-room by construction: `pattern_load`
     *    is a fraction of the frame, not a property of one thing in it. */
    if (typeof load === 'number') {
      if (load < 0.15) {
        used.pattern = 1;
        out.push('Almost nothing in it carries a pattern.');
      } else if (load > 0.6) {
        used.pattern = 1;
        out.push('There is a lot going on across the surfaces already.');
      }
    }
    /* 4. THE PALETTE, ONLY IF THERE IS ROOM AND ONLY AS A WHOLE. */
    if (out.length < 3 && r.is_neutral === true) {
      used.neutral = 1;
      out.push('The whole palette is neutral.');
    }
    return out.slice(0, 3);
  }

  /* ── WHY IT SCORED THAT, AND WHAT KIND OF RUG IT NEEDS ───────────────────
   * Two lists under one score, and they answer two different questions:
   * `scoreBullets` explains THE NUMBER, `rugNeeds` prescribes THE RUG. Both
   * are built from fields the analyser actually returned and from the two
   * canvas reads taken here - there is no line in either that is a general
   * rule about rugs, and there is no line that is padding.
   *
   * `roomWants` STOOD HERE UNTIL 2026-09-07 and listed what the room was short
   * of. It was true and it was unconnected to the number above it, so a
   * customer read 5.4 and then read four observations that did not add up to
   * 5.4. It is replaced rather than supplemented: two lists of gaps under one
   * score is the page saying the same thing twice.
   *
   * NOTHING IS SAID TWICE ON THIS SCREEN. The paragraph above spends the
   * strongest facts and records which in `spent`; the bullets phrase a spent
   * term differently rather than repeating it. That is the same discipline
   * `composeWhy` uses across the ten cards, applied one level up.
   *
   * WHERE A FACT IS MISSING THE LINE IS ABSENT, NEVER GENERIC. An `unclear`
   * field is not an invitation to guess: it is already named in the red frame's
   * other branch, and a room that yields two observations gets two.
   */
  var WANTS_MAX = 4;

  /* ── THE RED BULLETS EXPLAIN THE NUMBER ─────────────────────────────────
   * By direct decision of the user (2026-09-07): "Then explain why it scored
   * what it scored - in red bullets, one point each, and specific enough that
   * a person recognises their own room in it."
   *
   * WHAT THEY WERE. "What this room is short of right now" - a list of gaps,
   * true, and unconnected to the number directly above them. A customer read
   * 5.4 and then read four observations that did not add up to 5.4.
   *
   * WHAT THEY ARE NOW. One bullet per SCORED TERM, carrying the points that
   * term actually cost, in the order of what it cost. The score is
   * `7.0 - 2.0 x (1 - w)` over three weighted terms, so the arithmetic is
   * already there and the bullets are it, read out. Nothing is invented and
   * nothing is a general rule about rugs: a term that scored well is not
   * listed, because it did not cost anything to explain.
   *
   * THEY ARE WHOLE-ROOM, LIKE THE PARAGRAPH, AND FOR THE SAME REASON. The
   * anchor term is the floor's lightness and it is the largest single weight -
   * but it is reported as what the room lacks (something to hold it down)
   * rather than as a claim about the floor, because the floor is exactly the
   * thing the reading gets wrong. "Nothing in this room is dark enough to hold
   * it down" survives a misread material; "your light wood floor is pale" does
   * not.
   *
   * THE COST IS SHOWN BECAUSE IT IS THE RECOGNISABLE PART. A bullet saying
   * "this is what cost you the most" is checkable against the number above it,
   * and the points are the one thing on this screen that is arithmetic rather
   * than description.
   *
   * NOTHING IS SAID TWICE. The paragraph spends the strongest facts and
   * records which in `spent`; a term already spent there is phrased
   * differently here rather than repeated, and one that was not is free. */
  function scoreBullets(r, rs, spent) {
    var used = spent || {};
    if (!rs || !rs.scored || rs.why !== 'measured' || !rs.terms) return [];
    var t = rs.terms;
    var rows = [];

    /* Points lost = SWING x weight x (1 - term). The same three numbers the
     * score is built from, so a bullet cannot disagree with the figure above
     * it. Rounded to one decimal, which is the precision the score prints. */
    function cost(w, v) { return Math.round(SCORE_SWING * w * (1 - v) * 10) / 10; }

    if (t.anchor !== null && t.anchor !== undefined) {
      rows.push({
        c: cost(SCORE_W.anchor, t.anchor),
        s: t.anchor < 0.5
          ? 'There is nothing dark in the room to hold it down. The eye has ' +
            'no low note to rest on, so the whole space floats.'
          : 'The room already has something dark holding it down, which is ' +
            'most of why it scores as well as it does.'
      });
    }
    if (t.separation !== null && t.separation !== undefined) {
      rows.push({
        c: cost(SCORE_W.separation, t.separation),
        s: t.separation < 0.35
          ? 'The tones all sit on top of each other. Nothing in the room is ' +
            'far enough from anything else to read as a separate layer.'
          : 'The tones are already spread apart, so the room reads as more ' +
            'than one surface.'
      });
    }
    if (t.pattern !== null && t.pattern !== undefined) {
      var load = r.pattern_load;
      rows.push({
        c: cost(SCORE_W.pattern, t.pattern),
        s: load < 0.15
          ? 'Every surface is plain. A room with nothing to look at closely ' +
            'reads as unfinished rather than as calm.'
          : load > 0.6
            ? 'There is a lot of pattern competing already, and no one thing ' +
              'is winning.'
            : 'There is just enough pattern in the room to hold the eye.'
      });
    }

    /* LARGEST COST FIRST. A customer looking for the one thing to change
     * should find it on the first line. A term that cost nothing sorts last
     * and is dropped by the cap below rather than by a special case. */
    rows.sort(function (a, b) { return b.c - a.c; });

    return rows.filter(function (x) { return x.c > 0; })
               .slice(0, WANTS_MAX)
               .map(function (x) { return x.s + ' (-' + x.c.toFixed(1) + ')'; });
  }

  /* THE PRESCRIPTION. Each line carries the ENGINE'S OWN AXIS NAME, because
   * `composeWhy` is handed the axes spent here and stops the ten cards below
   * repeating them - that contract is what `jobsFrom` used to satisfy and it
   * is unchanged. What changed is where the words come from: `jobsFrom`
   * harvested LEAD clauses the ranking had already written, which meant this
   * block could only ever say what the top rug happened to be good at. These
   * are read off the ROOM, so they are the same list whichever rug ranks
   * first - which is what "what kind of rug this room needs" actually asks. */
  var NEEDS_MAX = 4;

  function rugNeeds(r, rs) {
    var out = [];
    var fl = window.FitEngine.floorPhrase(idx, { floorMaterial: r.floor_material });

    /* TONE, AND IT REVERSES BETWEEN TWO REAL PHOTOGRAPHS. A pale floor needs
     * a darker rug or the rug disappears into it; a dark floor is already
     * holding the room down and can take a lighter one. This is the largest
     * single effect in the whole ranking and it is the first thing said. */
    if (rs.floorL !== null && rs.floorL !== undefined) {
      out.push({ axis: 'tone', text: rs.floorL >= idx.floor_light_min_l
        ? 'Darker than your ' + fl + '. A pale rug on a pale floor disappears.'
        : rs.floorL < idx.floor_dark_max_l
          ? 'Lighter than your ' + fl + ', to lift the room off it.'
          : 'A clear step darker than your ' + fl + ', so the two do not blur.' });
    }
    /* COLOUR. `is_neutral` false means the room already has a colour story and
     * the rug has to join it rather than start one. */
    if (r.is_neutral === true) {
      out.push({ axis: 'family', text:
        'Real colour, not another neutral. The rug is the only place colour ' +
        'can come from in here.' });
    } else if (r.colour_family && r.colour_family !== 'unclear') {
      out.push({ axis: 'family', text:
        'Something that sits with the ' + r.colour_family + ' already in the ' +
        'room rather than fighting it.' });
    }
    /* PATTERN. Both directions are real answers and they are opposite. */
    if (typeof r.pattern_load === 'number') {
      if (r.pattern_load < 0.15) {
        out.push({ axis: 'pattern', text:
          'Something with a pattern in it. A plain rug adds nothing to a room ' +
          'this plain.' });
      } else if (r.pattern_load > 0.6) {
        out.push({ axis: 'pattern', text:
          'Quiet. There is enough going on in here already.' });
      }
    }
    /* SIZE, FROM THE THING THAT SIZES IT. `sofa_seats` is the field the band
     * is built from, and it is the most expensive one to get wrong. */
    if (typeof r.sofa_seats === 'number' && r.sofa_seats > 0) {
      out.push({ axis: 'size', text:
        'Wide enough for the front legs of your ' + r.sofa_seats +
        '-seat sofa to stand on it.' });
    }
    return out.slice(0, NEEDS_MAX);
  }

  /* ── bands: looked up, never computed, and now looked up in ONE place ───
   * `bandFor`, `inputKeyFor`, `parseSizes` and `fits` MOVED to fit-engine.js
   * on 2026-09-05, when the visualiser popup became a second caller. They are
   * delegated rather than copied for the reason this repo has paid for three
   * times over - two implementations of one rule is how the two come to
   * disagree, and here disagreeing means two surfaces telling the same
   * customer to buy two different sizes for the same room. */
  function bandFor(room, placement, key) {
    return window.FitEngine.bandFor(idx, room, placement, key);
  }

  /* ── which sizes a product can be made in that satisfy the band ─────────── */
  /* ── sizes read in inches, because the customers are American ──────────── */
  /* THE INDEX ALREADY CARRIES THE IMPERIAL LABEL. Every entry in `size_pools`
   * is the EXACT Shopify Size option string and it is imperial-first:
   *     2'7" x 3'3"  (80x100 cm)
   * so nothing has to be converted or rounded here — the number a customer sees
   * on the product page and the number the finder shows are the same string,
   * from the same source, and cannot drift apart. Taking the part before the
   * bracket is the whole job.
   *
   * `w` and `l` stay in CENTIMETRES throughout the engine. They are what the
   * size bands are expressed in and what the sort is computed on; converting
   * them would mean re-deriving every published band. Only the DISPLAY changes. */
  function imperial(option) {
    var s = String(option || '');
    var i = s.indexOf('(');
    return (i > 0 ? s.slice(0, i) : s).replace(/\s+/g, ' ').trim();
  }

  /* THE METRIC HALF OF THE SAME STRING, taken from inside the bracket. Like
   * `imperial` it CONVERTS NOTHING - the catalogue's own Size option value is
   * imperial-first with the centimetres in brackets, so both halves of the
   * size block are slices of the one string that also names the variant, the
   * cart line and the product page. Mirrors `rug-visualiser.metricLabel`; the
   * two are three lines each and neither can drift from the other without
   * drifting from the catalogue first. */
  function metricLabel(option) {
    var m = /\((\d+\s*[xX]\s*\d+\s*cm)\)/.exec(String(option || ''));
    return m ? m[1].replace(/\s+/g, ' ') : '';
  }

  function sizesFor(p, b) {
    return window.FitEngine.sizesInBand(p.buy || idx.size_pools[p.pool] || [], b);
  }

  /* ── THE SAME PHOTOGRAPH GETS THE SAME READING ────────────────────────
   * Not "usually the same": the same one, because it is not asked twice.
   *
   * The reading is cached against the EXACT BYTES that would be sent - the
   * 1024px q0.82 JPEG, not the file - so a re-upload of the same photograph
   * is a cache hit whatever the file is called and wherever it came from.
   * Identity is the bytes, which is the rule the reference pool already
   * follows for the same reason: a filename is not an identifier.
   *
   * WHY THIS EXISTS ON TOP OF `temperature: 0` IN THE WORKER. Temperature 0
   * removes the sampling; it is not a promise from a hosted model that two
   * requests can never differ. A customer re-uploading to check something is
   * the case that was reported, and for that case this is exact rather than
   * very likely - and it costs nothing, spends nothing, and returns instantly.
   *
   * sessionStorage, NOT localStorage, and that is deliberate. The reading
   * describes the inside of somebody's home. It lives as long as the tab and
   * no longer; nothing is written to disk and nothing survives the visit. The
   * Worker still stores nothing at all.
   *
   * A KEY IS A HASH, NEVER THE IMAGE. FNV-1a over the base64, plus the length
   * - enough to tell one upload from another within one visit, and not
   *   something an image can be recovered from. */
  var READ_CACHE_PREFIX = 'rz-room-read:';

  function readKey(b64) {
    var h = 0x811c9dc5;
    for (var i = 0; i < b64.length; i++) {
      h ^= b64.charCodeAt(i);
      h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
    }
    return READ_CACHE_PREFIX + b64.length.toString(36) + '-' + h.toString(36);
  }

  function cachedRead(k) {
    try {
      var v = sessionStorage.getItem(k);
      return v ? JSON.parse(v) : null;
    } catch (e) { return null; }        /* private mode, or a quota refusal */
  }

  function cacheRead(k, r) {
    /* ONLY A SUCCESSFUL READ IS KEPT. A refusal, a rate limit or an upstream
     * fault is a fact about that MOMENT, not about the photograph, and caching
     * one would make a transient failure permanent for the rest of the visit. */
    if (!r || !r.ok) return;
    try { sessionStorage.setItem(k, JSON.stringify(r)); } catch (e) { /* full */ }
  }

  /* ── the run ─────────────────────────────────────────────────────────── */
  function analyse(p) {
    /* THE ENCODE IS THE `preparing` PHASE and it is genuinely a step: the
     * canvas is downscaled to 1024 and re-encoded before anything leaves the
     * browser. Measured at ~19ms, so the bar passes through it rather than
     * resting on it - but a phase that exists is a phase that gets reported. */
    phase('preparing');
    /* AN UNCONFIGURED ANALYSER MUST SAY SO, NOT FAIL AS A NETWORK ERROR.
     * Liquid renders an unset `url` setting as null, so `fetch(null)` resolves
     * the literal string "null" against the storefront origin - the customer's
     * room photo is POSTed into the shop's own 404 page and `r.json()` throws
     * on the HTML. The customer sees a generic failure and nobody can tell a
     * missing SETTING from a broken Worker. This is the one configuration
     * mistake that is certain to happen at least once, because the URL does not
     * exist until the Worker is deployed. */
    if (!ANALYSER || typeof ANALYSER !== 'string' || !/^https?:\/\//i.test(ANALYSER)) {
      return Promise.resolve({
        ok: false, reason: 'not_configured',
        message: 'The photo reader is not set up yet.'
      });
    }
    var b64 = downscaleToBase64(p);
    var key = readKey(b64);
    var hit = cachedRead(key);
    if (hit) return Promise.resolve(hit);

    return fetch(ANALYSER, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ image: b64, media_type: 'image/jpeg' })
    }).then(function (r) {
      /* A NON-JSON BODY IS A DEPLOYMENT FAULT, NOT A ROOM VERDICT. An HTML 404
       * or a proxy error page would otherwise throw inside .json() and surface
       * as an unexplained failure. */
      return r.json().catch(function () {
        return { ok: false, reason: 'upstream',
                 message: 'The photo reader returned something unexpected (' +
                          r.status + ').' };
      }).then(function (d) { cacheRead(key, d); return d; });
    });
  }

  /* The first layout for this room that a band answers AND that something we
   * stock can fill. Falls back to the most-recommended one, so a room where
   * nothing fits still reports that layout's own reason rather than none. */
  function choosePlacement(room) {
    var list = idx.placements[room] || [];
    for (var i = 0; i < list.length; i++) {
      var b = bandFor(room, list[i], state.sizeKey);
      if (!b || !b.ok) continue;
      var n = 0;
      for (var j = 0; j < idx.products.length && n < MIN_VIABLE; j++) {
        if (sizesFor(idx.products[j], b).length) n++;
      }
      if (n >= MIN_VIABLE) return list[i];
    }
    return list[0];
  }

  function inputKeyFor(room, r) {
    return window.FitEngine.inputKeyFor(room, r);
  }

  function run(p) {
    phase('reading');
    return analyse(p).then(function (r) {
      if (!r.ok) return refuse(r);
      read = r;
      var room = (r.room && r.room !== 'unclear') ? r.room : 'living';
      state.room = room;
      phase('measuring');
      var floor = anchoredFloorL(p, r);
      var posterior = window.FitEngine.floorPosterior(
        idx, r.floor_material, floor.L, floor.anchored ? 6 : 12);

      /* THE SECOND CANVAS READ, and the room's own score off the two of them.
       * Free: no call, no latency, and the full-resolution canvas is already
       * here. `floor.shift` is the white-anchor correction, passed through so
       * the floor and the frame are on ONE scale. */
      var shift = floor.anchored ? (floor.L - floor.raw) : 0;
      var frame = sampleFrame(p, r.floor_region, shift);
      roomRead = roomScore(p, r, floor, frame);
      phase('weighing');

      state.sizeKey = inputKeyFor(room, r);
      /* THE SYSTEM PICKS THE LAYOUT, AND IT PICKS ONE THAT HAS SOMETHING TO
       * SHOW. `placement.py` returns them most-recommended first, so this takes
       * the first ordinarily - but a band can legitimately refuse (an
       * all-legs-on layout for a long sofa needs more than the loom can weave)
       * or admit nothing we stock in that room.
       *
       * IT USED TO TELL THE CUSTOMER "Try instead: floating" and offer a chip.
       * With the chips gone that sentence would name something they cannot do,
       * so the fall-through happens here instead. This is `match.choose_
       * placement`'s reasoning applied at the only place that now decides. */
      state.placement = choosePlacement(room);

      /* NAMED `forEngine`, NOT `roomRead`. The module-level `roomRead` is the
       * room's SCORE; this is what the RANKER is handed, and they carry
       * different things. Two objects one letter apart is how a later edit
       * hands the wrong one to the wrong function. */
      var forEngine = {
        style: r.style === 'unclear' ? null : r.style,
        floorSamples: posterior,
        floorL: posterior.length
          ? posterior.slice().sort(function (a, b) { return a - b; })[
              Math.floor(posterior.length / 2)]
          : null,
        colourFamily: r.colour_family,
        isNeutral: r.is_neutral,
        patternLoad: r.pattern_load,
        /* THE FOUR FIELDS THE COLOUR REBUILD ADDED (2026-09-07). Every one is
         * null when the analyser could not read it, and every term that reads
         * one ABSTAINS on null rather than defaulting - so a deployed client
         * against an OLDER analyser scores exactly as it did before, with the
         * new terms simply dropped. That is what makes this safe to ship
         * before the Worker. */
        floorUndertone: r.floor_undertone || null,
        colourSecondary: r.colour_secondary || null,
        secondaryStrength: r.secondary_strength || null,
        roomComplexity: r.room_complexity || null,
        /* THE MATERIAL, SO A REASON CAN NAME THE FLOOR THE CUSTOMER IS LOOKING
         * AT. Without it `floorPhrase` falls back to a bare "your floor" - which
         * is true, and which throws away the one detail that proves we read
         * their photograph rather than guessed. Caught on the live page: the
         * read-back line said "mid wood floor" in the same view where every
         * card said "your floor". */
        floorMaterial: r.floor_material || null,
        /* THE ROOM TYPE, AND IT IS THE FIRST TIME IT HAS REACHED THE RANKER
         * (2026-09-07). It drives ONE thing there - the guide's construction
         * exclusions, which refuse a rug too thick for a door to clear or a
         * caster to roll over. See the note under readBack(): a property this
         * file recorded as STRUCTURAL is now MEASURED instead, and the
         * measurement is zero.
         *
         * THE RAW VALUE, NOT `state.room`. That one falls back to 'living'
         * when the analyser says "unclear", which is right for choosing a size
         * and wrong here: 'living' carries no rule, so a fallback would read
         * as a decision not to exclude rather than as an abstention. Null is
         * the honest input and the engine abstains on it. */
        room: (r.room && r.room !== 'unclear') ? r.room : null
      };
      phase('choosing');
      rank(forEngine);

      /* A THIN READ IS NOT ROUTED ANYWHERE ANY MORE, AND THAT FOLLOWS FROM
       * THE SCREENS RATHER THAN FROM A CHANGE OF MIND. There used to be a
       * guidance screen to send somebody back to, and a skip link that decided
       * whether they had already seen it. Both are gone: the upload IS the
       * second screen, and what could not be read is said on the next screen,
       * in its own red frame, above the rugs it affects. */
      go(3, true);
    }).catch(function (e) {
      show('problem', { message: 'We could not reach the photo reader. ' +
                                 'You can still browse the collection.',
                        detail: String(e && e.message || e) });
    });
  }

  /* TEN, AND EVERY ONE A DIFFERENT RUG.
   *
   * `sizesFor` is handed to the engine because the collapse from one row per
   * (product, SHAPE) to one row per PRODUCT has a tie the score cannot break:
   * `scoreOne` reads only properties of the ARTWORK, so a product's rectangle
   * and runner rows arrive at the same number. The engine breaks it on the
   * biggest rug each shape can put in THIS room, which needs this room's band -
   * so the band has to travel in rather than be guessed at. */
  /* THE PARAMETER IS THE ENGINE'S ROOM OBJECT, AND IT IS NAMED FOR THAT.
   * It used to be called `roomRead`, which is the name of the module-level
   * variable holding the room's SCORE - two different objects, one letter
   * apart, one shadowing the other inside this function. `roomStories` needs
   * the engine's object (it carries `floorL` and `isNeutral`) and would have
   * been handed the score by anybody reading `results.roomRead` and believing
   * the name, so it is stored under its real one. */
  function rank(forEngine) {
    /* A NEW PHOTOGRAPH STARTS AT THE FIRST TEN. Without this a customer who
     * uploaded a second room would open on the rotated window of the first
     * one's ranking - the offset is a property of THIS result set. */
    state.tenOffset = 0;
    stopSwap();
    band = bandFor(state.room, state.placement, state.sizeKey);
    var candidates = idx.products.filter(function (p) {
      return sizesFor(p, band).length > 0;
    });
    results = { roomRead: forEngine, forEngine: forEngine, band: band,
                out: window.FitEngine.rank(idx, candidates, forEngine, {
                  top: RESULTS_POOL,
                  sizesFor: function (p) { return sizesFor(p, band); }
                }) };
    render();
  }

  /* ── A PHOTOGRAPH THAT CANNOT WORK GETS A DIALOG, NOT A STATUS LINE ──────
   * By direct decision of the user: explain what failed and what to send
   * instead, specific to the failure, and let them upload again without losing
   * their place.
   *
   * ONLY A VERDICT ABOUT THE PHOTO OPENS IT (§12). `daily_cap`, `rate`,
   * `upstream`, `not_configured` and `origin` are facts about US or about a
   * limit - none of them is a finding about the customer's picture, and
   * offering "here is what to send instead" for a spend cap would be a code
   * fault dressed as a product verdict. Those keep the status box.
   *
   * THE RETRY INPUT IS INSIDE THE DIALOG and is bound by the same `data-fit-file`
   * handler as the drop box, so there is ONE path from a file to a result. A
   * second uploader is how the two would come to differ. */
  var PHOTO_FAULTS = { not_a_room: 1, declined: 1, no_read: 1 };

  var FAULT_COPY = {
    not_a_room: {
      title: 'We could not find a room in that photo',
      body: 'We need to see a floor to work out what size rug fits. A photo ' +
            'of a rug on its own, a person, or a screenshot with no room in ' +
            'it will not do it. Stand in the doorway and take in the whole ' +
            'space, floor included.'
    },
    declined: {
      title: 'We could not read that photo',
      body: 'Try one that shows more of the room and less of the people in ' +
            'it. The floor and the furniture are what we read.'
    },
    no_read: {
      title: 'We could not read that photo',
      body: 'Nothing came back we could use. A wider, brighter shot of the ' +
            'same room usually works.'
    }
  };

  function refuse(r) {
    var dlg = $('[data-rf-dialog]');
    if (!PHOTO_FAULTS[r.reason] || !dlg || !dlg.showModal) {
      return show('problem', friendly(r));
    }
    var c = FAULT_COPY[r.reason] || FAULT_COPY.no_read;
    $('[data-rf-dialog-title]', dlg).textContent = c.title;
    $('[data-rf-dialog-body]', dlg).textContent = c.body;
    $('[data-rf-dialog-retry]', dlg).textContent = 'Choose another photo';
    /* THE "SHOW ME WHAT WORKS" BUTTON WENT WITH THE GUIDANCE SCREEN. There is
     * nowhere to send somebody: the two examples are on the upload screen this
     * dialog opens over, and re-choosing a photo is the only action left that
     * changes anything. */
    var box = $('[data-fit-status]', root);
    if (box) box.hidden = true;
    stopProgress();
    try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
  }

  function friendly(r) {
    var m = {
      declined: 'We could not read this photo. Try one that shows more of the ' +
                'room and less of the people in it.',
      not_a_room: 'That does not look like a photo of a room. Try a wider shot ' +
                  'that shows the floor.',
      too_large: 'That photo is very large. Try one straight from your camera roll.',
      daily_cap: 'The photo reader has reached its limit for today. It resets at ' +
                 'midnight — you can still browse the collection.',
      rate: 'That is a lot of photos in one hour. Give it a few minutes.',
      upstream: 'The photo reader is unavailable right now.',
      budget_unavailable: 'The photo reader is unavailable right now.',
      /* SAID PLAINLY, because this one is OURS. Every other message here
       * describes something about the photo or the service; this describes a
       * setting nobody filled in, and a customer reading "we could not read
       * this photo" would reasonably keep trying other photos forever. */
      not_configured: 'The photo reader is not switched on yet. It is being set ' +
                      'up — you can still browse the collection.',
      origin: 'The photo reader is unavailable right now.'
    };
    return { message: m[r.reason] || 'We could not read this photo.',
             detail: null };
  }

  /* A RAW FIELD NAME MUST NEVER REACH A CUSTOMER. This printed "We could not
   * tell floor_material from this photo" on the live storefront. The map is
   * TOTAL by construction - anything unmapped falls back to a readable form of
   * itself rather than leaking the identifier. */
  var FIELD_LABEL = {
    floor_material: 'what your floor is made of',
    colour_family: 'the colours in the room',
    style: 'the style of the room',
    /* KEPT, because `fieldLabel` is TOTAL by construction and a raw
     * `room` reaching a customer is the fault it exists to prevent. It is
     * filtered out before display rather than removed from the map. */
    room: 'the layout of the space',
    pattern_load: 'how much pattern the room already has',
    sofa_seats: 'the size of your sofa',
    bed_size: 'the size of your bed',
    table_seats: 'how many the table seats'
  };
  function fieldLabel(k) {
    return FIELD_LABEL[k] || String(k).replace(/_/g, ' ');
  }

  /* THE IMAGE URL COMES FROM THE INDEX, NOT FROM THE PRODUCT'S NAME.
   * `imageBase.replace('{name}', p.n)` served manufacturer RPBR009 "Thicket"
   * the AI design Thicket's photograph - 30 names are shared between the two
   * sources, because design names are released and reused. The url returned
   * 200, so nothing looked broken; the customer was simply shown a different
   * rug from the one being recommended, in the card AND in the preview.
   * The pattern remains only as a fallback for an index built before `img`. */
  function imageFor(p) {
    if (p && p.img) return p.img;
    if (!CFG.imageBase) return null;
    return CFG.imageBase.replace('{name}', (p && (p.n || p.sku)) || '')
                        .replace('{shape}', (p && p.sh) || '');
  }


  /* ── buying the thing we just recommended ──────────────────────────────── */
  /* THE SIZE OPTION STRING IS THE JOIN KEY, AND IT IS EXACT. Every entry in the
   * published size pool is byte-identical to a Shopify Size option value, and
   * measured on the live store each Size value belongs to exactly ONE variant -
   * bbb102-willow has 2 shapes x 335 sizes and 335 variants, not 670, because
   * the rectangle sizes and the runner sizes are different strings. So
   * (handle, sizeString) identifies a variant with no SKU parsing, which is
   * just as well: the two sources spell SKUs differently
   * (BBB102-RECT-24x47-60x120cm against rpvy07-80x100cm-31x39in-Rectangle).
   * Verified: 78/78, 257/257 and 5/5 of three real pools resolve.
   *
   * THE PRODUCT .js ENDPOINT TRUNCATES AT 250 VARIANTS AND SAYS NOTHING. On
   * bbb102-willow it returns 250 rows, ALL of them Rectangle - every Runner and
   * 85 rectangle sizes simply absent. Matching against one page would fail
   * silently for a large part of the catalogue and, worse, could match nothing
   * while looking like a sold-out product. `?page=N` returns the rest, so the
   * fetch pages until a short page arrives.
   *
   * FETCHED ON DEMAND, MEMOISED PER HANDLE. Six cards x up to two pages on
   * render would be ~180 KB nobody asked for; this costs one CDN-cached request
   * at the moment a customer actually reaches for a size. */
  var VARIANTS = {};
  var VAR_PAGE = 250;

  /* Resolves ONE size string to a variant, paging only as far as it must.
   *
   * IT DISTINGUISHES "NOT LISTED" FROM "COULD NOT CHECK", AND THAT DISTINCTION
   * IS THE WHOLE REASON THIS IS NOT A ONE-LINER. The first version treated any
   * unsuccessful fetch as an empty variant list, so a customer whose request
   * was RATE LIMITED - Shopify answers /products/<handle>.js with HTTP 429 and
   * an HTML throttle page - was told "that size is not listed for this rug".
   * That is a statement about the catalogue, it was false, and it was produced
   * by an infrastructure fault. Measured during testing, on the real endpoint.
   *
   * So a transport failure REJECTS with 'lookup_failed' and a completed lookup
   * that simply does not contain the size resolves to null. The card says two
   * different things for those two cases because they are two different facts.
   *
   * IT STOPS AT THE FIRST PAGE THAT CONTAINS THE ANSWER. Most products fit in
   * one page; only a 335-size design needs a second, and only then if the size
   * is a runner. Fewer requests is also fewer chances of being throttled. */
  function resolveVariant(handle, optionString) {
    var cache = VARIANTS[handle] || (VARIANTS[handle] = { seen: {}, done: false, pages: 0 });
    if (cache.seen[optionString]) return Promise.resolve(cache.seen[optionString]);
    if (cache.done) return Promise.resolve(null);

    function page(n) {
      var url = '/products/' + encodeURIComponent(handle) + '.js'
              + (n > 1 ? '?page=' + n : '');
      return fetch(url, { headers: { accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('lookup_failed');
          return r.json();
        })
        .then(function (d) {
          /* THE PRODUCT ID COMES FREE WITH THIS LOOKUP, and Loox needs it.
           * `/products/<handle>.js` carries `id` at the top level, so the
           * review stars cost NO REQUEST OF THEIR OWN - they ride the call the
           * price already makes. Adding the id to the fit index instead was
           * rejected for the reason the compare-at price was: a rebuild and
           * republish of an index this file reads at 27 KB gzipped, to save a
           * repaint that already happens. */
          if (d && d.id) cache.pid = d.id;
          /* THE DETAILS POPUP'S CONTENT COMES OFF THIS SAME RESPONSE. `media`
           * rather than `images`: it carries the ALT, and the alt is the
           * routing token this store files every product photograph by - the
           * shape, and the withheld-hero marker. `images` is urls alone and
           * cannot be filtered. `description` is PLAIN TEXT on this endpoint
           * (verified: zero tags across the catalogue), which is why it is set
           * with textContent and never innerHTML. */
          if (d && !cache.media) {
            cache.media = (d.media || []).filter(function (m) {
              return m && m.media_type === 'image' && m.src;
            }).map(function (m) {
              return { src: m.src, alt: String(m.alt || ''), pos: m.position || 0 };
            });
            cache.desc = String(d.description || '');
            cache.title = String(d.title || '');
            /* THE PRODUCT TYPE, FOR THE CUSTOMER-FACING NAME. `window.RugName`
             * needs the title and the type, and this response carries both -
             * so the short name costs no request of its own either. */
            cache.type = String(d.type || '');
          }
          var vs = (d && d.variants) || [];
          vs.forEach(function (v) {
            (v.options || []).forEach(function (o) {
              /* Only the SIZE option carries a bracketed figure, so matching on
               * that rather than on options[1] survives a product whose options
               * are ordered differently. */
              if (String(o).indexOf('(') !== -1) cache.seen[o] = v;
            });
          });
          cache.pages = n;
          if (cache.seen[optionString]) return cache.seen[optionString];
          if (vs.length >= VAR_PAGE && n < 8) return page(n + 1);
          cache.done = true;
          return null;
        });
    }
    return page(1);
  }

  /* ── THE REVIEW STARS: LOOX, UNDER THE MODEL NAME (2026-09-07) ────────────
   * By direct decision of the user, after checking what Loox actually holds.
   * Measured across all 561 products in the catalogue on 2026-09-07: EVERY ONE
   * carries a rating, 25 to 90 raters, median 54, on both sources. So the
   * empty-star-row failure the check was for cannot happen - but the widget is
   * still drawn only when Loox answers with a count, because "cannot happen
   * today" is not a rule and a new product will exist before its first review.
   *
   * ── THE MECHANISM MOVED TO `assets/rug-loox.js` (2026-09-07) ──────────────
   * The debounce, the per-view memo, the bulk-endpoint URL and the "no count,
   * no stars" rule were all inline here until the stars were put on every card
   * surface on the site. The favourites page builds its cards in the browser
   * too and needed every one of those behaviours identically; writing them a
   * second time is the shape CLAUDE.md records for `rug-cart.js`, where one
   * inert dispatch written twice meant one of them being wrong meant both
   * were. `window.RugLoox` is the one definition and this is its adapter -
   * the only thing the finder knows that the module does not is that a card's
   * product id is stashed on `VARIANTS[handle]`.
   *
   * ONE CALL SERVES THE WHOLE PAGE. The module scans `document` rather than a
   * caller's root, so the ten result cards and the details popup share one
   * request - and a Liquid-rendered card, which already carries its count from
   * a metafield, is skipped and costs nothing.
   *
   * IT COSTS NO REQUEST OF ITS OWN. The product id is stashed by
   * `resolveVariant`, which already runs for the price. If that lookup fails
   * or is refused there is no id, no widget and no gap - the card is exactly
   * as it was before this existed. */
  /* ── THE CUSTOMER-FACING NAME ─────────────────────────────────────────────
   * `window.RugName` is the rule; `snippets/rug-short-name.liquid` renders the
   * identical string on every Liquid surface. The finder is one of the two
   * places that cannot use the snippet - it builds its cards from the fit
   * index, which carries the MODEL NAME (`p.n`) and no title at all.
   *
   * SO THE NAME ARRIVES WITH THE PRICE. The full title and the product type
   * both come off `/products/<handle>.js`, the call `priceFor` already makes,
   * and until it lands the card shows `p.n` - which is exactly what it showed
   * before this existed. There is no request for this and no empty heading at
   * any point.
   *
   * THE SETTLE IS BEHIND THE CURTAIN. Cards are built when the ranking
   * finishes, on screen 3, hidden, and the lookups land 140-209ms later; the
   * customer reaches screen 4 to a name that is already right. */
  function shortName(p, cache) {
    if (cache && cache.title && window.RugName) {
      var n = window.RugName.from(cache.title, cache.type);
      if (n) return n;
    }
    return p.n || p.sku || '';
  }

  function looxStars(handle) {
    var cache = VARIANTS[handle];
    if (!cache || !cache.pid) return null;
    if (!window.RugLoox) return null;
    return window.RugLoox.widget(cache.pid, 'fit-card__stars');
  }

  /* Adds one line and tells the THEME, rather than reloading the page, so the
   * customer keeps their results instead of being navigated away from them.
   *
   * ── THE NOTIFICATION WAS INERT AND IS `assets/rug-cart.js` NOW (2026-09-06)
   * The comment here used to say that dispatching
   * `shopify:cart:lines-update` "is the supported way for custom add-to-cart
   * code to keep them in step". The NAME was right - it is exactly
   * `StandardEvents.cartLinesUpdate` - and the SHAPE was not: every listener
   * in this theme reads `event.promise`, a plain CustomEvent has none, and
   * `?.` swallowed it. MEASURED on the product page, where the visualiser
   * carried a byte-for-byte copy of the same dispatch: the server cart went
   * 7 -> 8 and the header bubble stayed at "0" and stayed hidden.
   *
   * Both surfaces are on the one helper now, which is also why this was
   * fixed here and not only where it was reported: it was ONE defect written
   * twice, and correcting one copy is how the two come to disagree. */
  function addToCart(variantId, qty) {
    if (window.RugCart && window.RugCart.add) {
      return window.RugCart.add(variantId, qty || 1, 'rug-finder');
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'cart'); },
                                      function () { throw new Error('cart'); });
      return r.json();
    });
  }

  /* The symbol comes from the SHOP, via Liquid - not a constant here, which
   * would be wrong the day a second currency exists. Both the carried
   * from-price and Shopify's own cents go through this, so the two figures on a
   * card can never be formatted differently from each other. */
  function money(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    return (CFG.currency || '') + n.toFixed(2);
  }

  /* ── rendering ───────────────────────────────────────────────────────── */
  var root;

  /* ── THE PROGRESS BAR ────────────────────────────────────────────────────
   * By direct decision of the user (2026-09-07), replacing a spinner: a bar, a
   * percentage beside it, and short lines cycling to its left saying what the
   * system is working through.
   *
   * ── WHAT IS MEASURED AND WHAT IS ESTIMATED, STATED RATHER THAN IMPLIED ────
   * The six FLOORS below are real transitions - each one is reached when a
   * thing has actually finished. What is NOT measured is the movement between
   * them: the analyser is ONE fetch with no progress events, so nothing can
   * report how far through a vision call is. The bar therefore CREEPS toward
   * the next floor and can never reach it; only a real transition takes it
   * there.
   *
   * THAT DISTINCTION IS THE WHOLE DESIGN. This file's own notes on the
   * visualiser record why: a competitor's six-step bar advances on a
   * `setInterval` and narrates steps nobody performs, and part of why their
   * feature reads as flawless is a UX construction rather than a measurement.
   * A creep that cannot pass its floor is an honest "still working"; a creep
   * that hits 100% on a timer is a lie with a number on it.
   *
   * THE LINES ARE COPY, AND THEY ARE SETTINGS. A literal here would be a copy
   * slot `test_placeholder_markers` structurally cannot see. Each one is tied
   * to the phase that produces the thing it names.
   *
   * ONE HONESTY NOTE ON LINES 3 TO 5. The analyser answers everything in a
   * SINGLE call, so within that phase the lines describe WHAT WE ASKED FOR, in
   * the order the tool schema lists it - not a sequence of steps happening one
   * after another. That is a report of the request. It is written down here so
   * nobody later reads the cycling as a narration of work.
   */
  /* READ FROM THE DOM, NOT FROM `RUG_FINDER_CONFIG`. They were JSON in that
   * object first; `test_placeholder_markers` cannot see a setting that reaches
   * the client that way, so six numbered copy slots carried no outline test.
   * They are six hidden `<p>`s now, each with its own test, and this reads
   * them. */
  function readSteps() {
    var out = [];
    var box = root && root.querySelector('[data-rf-steps]');
    if (!box) return out;
    var ps = box.querySelectorAll('[data-rf-step]');
    for (var i = 0; i < ps.length; i++) {
      var s = (ps[i].textContent || '').trim();
      if (s) out.push(s);
    }
    return out;
  }
  var STEPS = [];
  /* floor % , which STEP text belongs to it, and the measured typical seconds
   * that phase takes - the creep rate comes from the seconds, so a phase that
   * really is slow creeps slowly rather than parking. */
  var PHASES = [
    { key: 'opening',   floor: 8,   step: 0, secs: 0.5 },
    { key: 'preparing', floor: 18,  step: 1, secs: 0.5 },
    { key: 'reading',   floor: 30,  step: 2, secs: 6.0 },
    { key: 'measuring', floor: 78,  step: 4, secs: 0.3 },
    { key: 'weighing',  floor: 88,  step: 4, secs: 0.2 },
    { key: 'choosing',  floor: 100, step: 5, secs: 0.4 }
  ];
  /* The reading phase asks for several things at once, so it cycles the lines
   * that name them rather than sitting on one for six seconds. */
  var READING_STEPS = [2, 3];
  var CYCLE_MS = 2200;

  var prog = { pct: 0, phase: -1, timer: null, cycle: null, at: 0 };

  function progEls() {
    return {
      box:  $('[data-rf-progress]', root),
      line: $('[data-rf-progress-line]', root),
      bar:  $('[data-rf-progress-bar]', root),
      fill: $('[data-rf-progress-fill]', root),
      pct:  $('[data-rf-progress-pct]', root)
    };
  }

  function paintProgress() {
    var e = progEls();
    if (!e.fill) return;
    var v = Math.round(prog.pct);
    e.fill.style.width = v + '%';
    if (e.pct) e.pct.textContent = v + '%';
    if (e.bar) e.bar.setAttribute('aria-valuenow', String(v));
  }

  function setStep(i) {
    var e = progEls();
    if (!e.line) return;
    var txt = STEPS[i];
    if (!txt) { e.line.hidden = true; return; }
    e.line.hidden = false;
    e.line.textContent = txt;
    /* THE PLACEHOLDER FLAG TRAVELS WITH THE STRING, so an unfilled slot paints
     * its red outline where a reviewer would actually read it. */
    if (String(txt).indexOf('[PLACEHOLDER') === 0) {
      e.line.setAttribute('data-rz-placeholder', '');
    } else {
      e.line.removeAttribute('data-rz-placeholder');
    }
  }

  /* Move to a phase: jump to its floor, show its line, and start creeping
   * toward the NEXT floor at a rate derived from that phase's measured length.
   * The creep is capped a whisker short of the next floor, so arriving there
   * is always something that actually happened. */
  function phase(key) {
    var i = -1;
    for (var k = 0; k < PHASES.length; k++) if (PHASES[k].key === key) i = k;
    if (i < 0 || i < prog.phase) return;          /* never go backwards */
    prog.phase = i;
    var ph = PHASES[i];
    prog.pct = Math.max(prog.pct, ph.floor);
    paintProgress();

    var e = progEls();
    if (e.box) e.box.hidden = false;
    var box = $('[data-fit-status]', root);
    if (box) box.hidden = true;

    if (prog.timer) { clearInterval(prog.timer); prog.timer = null; }
    if (prog.cycle) { clearInterval(prog.cycle); prog.cycle = null; }

    if (ph.key === 'reading' && STEPS.length) {
      prog.at = 0;
      setStep(READING_STEPS[0]);
      prog.cycle = setInterval(function () {
        prog.at = (prog.at + 1) % READING_STEPS.length;
        setStep(READING_STEPS[prog.at]);
      }, CYCLE_MS);
    } else {
      setStep(ph.step);
    }

    var next = PHASES[i + 1];
    if (!next) return;
    var ceiling = next.floor - 1;
    var tick = 120;
    var rate = ((next.floor - ph.floor) / Math.max(0.2, ph.secs)) * (tick / 1000);
    prog.timer = setInterval(function () {
      if (prog.pct >= ceiling) return;
      prog.pct = Math.min(ceiling, prog.pct + rate);
      paintProgress();
    }, tick);
  }

  function stopProgress() {
    if (prog.timer) { clearInterval(prog.timer); prog.timer = null; }
    if (prog.cycle) { clearInterval(prog.cycle); prog.cycle = null; }
    prog = { pct: 0, phase: -1, timer: null, cycle: null, at: 0 };
    var e = progEls();
    if (e.box) e.box.hidden = true;
    if (e.fill) e.fill.style.width = '0%';
  }


  function show(kind, payload) {
    var box = $('[data-fit-status]', root);
    if (!box) return;
    box.hidden = false;
    box.className = 'fit-status fit-status--' + kind;
    box.textContent = '';
    if (kind === 'working') {
      box.appendChild(el('span', 'fit-spin'));
      box.appendChild(el('span', null, payload));
      return;
    }
    /* EXPLICIT FAILURE COPY, NEVER AN EMPTY BOX. A silently absent feature
     * cannot be reported by a customer either, so a failure that renders as
     * nothing is a failure nobody will ever tell us about. */
    box.appendChild(el('p', 'fit-status__msg', payload.message));
    if (payload.detail) box.appendChild(el('p', 'fit-status__detail', payload.detail));
    var a = el('a', 'fit-status__link', 'Browse all rugs');
    a.href = '/collections/all';
    box.appendChild(a);
  }

  /* WHICH FIELDS CAME BACK THIN. Named rather than a boolean, because the
   * banner on screen 2 says WHICH rule would have helped - "we could not tell
   * what your floor is made of" points at rule 2, and a generic "that was hard
   * to read" points at nothing. */
  function thinReasons(r) {
    var out = [];
    if (r.confidence === 'low') out.push('this photo was hard to read');
    var u = r.unclear || [];
    for (var i = 0; i < u.length; i++) {
      if (u[i] === 'room') continue;      /* substituted, not dropped - see sizeLine */
      out.push('we could not tell ' + fieldLabel(u[i]));
    }
    return out;
  }

  /* `guide()` WAS DELETED ON 2026-09-07 ALONG WITH THE SCREEN IT ROUTED TO.
   * It filled a banner at the top of the photography screen with the reason a
   * read came back thin and offered a button straight on to the results. With
   * the four rules gone there is no screen to route to, and the same reasons
   * are now printed on the reading itself - in the red frame under the score,
   * which is where somebody looking at a weak answer is already looking.
   * `thinReasons()` survives and is what fills it. */

  /* ── SCREEN 4 — WHAT WE SAW ──────────────────────────────────────────── */
  function paintRead() {
    /* THEIR PHOTOGRAPH, FIRST. Drawn from the canvas that is already in memory
     * rather than re-reading the file: it is the same pixels, and an object URL
     * would be a second handle on an image we have promised not to keep. */
    var ph = $('[data-rf-photo]', root);
    if (ph && photo) {
      ph.textContent = '';
      var im = document.createElement('img');
      im.className = 'rf-photo__img';
      im.alt = 'The room you photographed';
      var w = Math.min(1200, photo.w);
      var c = document.createElement('canvas');
      c.width = w; c.height = Math.round(photo.h * (w / photo.w));
      c.getContext('2d').drawImage(photo.canvas, 0, 0, c.width, c.height);
      im.src = c.toDataURL('image/jpeg', 0.85);
      ph.appendChild(im);
    }

    var sc = $('[data-rf-score]', root);
    var num = $('[data-rf-score-num]', root);
    if (sc && num) {
      /* NO SCORE FOR AN OUTDOOR SPACE, AND NO APOLOGY FOR IT EITHER. The block
       * is hidden; the rugs still come. A "we could not score this" line would
       * report a deliberate abstention as a failure. */
      sc.hidden = !(roomRead && roomRead.scored);
      if (roomRead && roomRead.scored) num.textContent = roomRead.score.toFixed(1);
    }

    /* ── THE PARAGRAPH ────────────────────────────────────────────────────
     * Written, not bulleted, by direct decision of the user: what kind of room
     * this is and what kind of touch it needs, understood on one pass. It is
     * BUILT FROM THE READING - a fixed string would be the same sentence for
     * every room, which is the one thing this screen must not be.
     *
     * IT DOES NOT NAME THE ROOM TYPE. The read gets that wrong often enough
     * that a patio came back as a living room, and the label was removed
     * deliberately; the room type still drives the SIZE and that is stated on
     * the next screen rather than hidden. */
    var spent = {};
    var said = $('[data-rf-said]', root);
    if (said) {
      said.textContent = '';
      if (roomRead && roomRead.scored) {
        said.appendChild(el('p', 'rf-said__p',
                            scoreReasons(read, roomRead, spent).join(' ')));
      }
    }

    /* ── THE RED FRAME: TWO CASES, AND THE SYSTEM PICKS ───────────────────
     * A photograph we could not read well lists what is wrong with THE
     * PHOTOGRAPH, so the customer knows why the answer is weaker than it could
     * be. One we read cleanly lists what THE ROOM needs. Both are things we
     * measured; neither is a general rule about rugs.
     *
     * THE ORDER MATTERS AND IT IS NOT ARBITRARY. A thin read is a statement
     * about our reading and it outranks a statement about their room - saying
     * "this room is very plain" off a photograph we admit we could not read is
     * a confident answer built on an unconfident one. */
    var flags = $('[data-rf-flags]', root);
    if (flags) {
      flags.textContent = '';
      var thin = thinReasons(read);
      var lines, lead;
      if (thin.length) {
        lead = 'This photo was harder to read than most, so the answer below ' +
               'is a rough one:';
        lines = thin.map(function (s) { return s.charAt(0).toUpperCase() + s.slice(1) + '.'; });
      } else {
        /* THE LEAD NAMES THE NUMBER THE BULLETS EXPLAIN, so the two blocks are
         * visibly one statement rather than a score and an unrelated list. */
        lead = roomRead && roomRead.scored
             ? 'Why your room scores ' + roomRead.score.toFixed(1) + ':'
             : 'Why your room scores what it does:';
        lines = roomRead && roomRead.scored
              ? scoreBullets(read, roomRead, spent) : [];
      }
      if (lines.length) {
        flags.hidden = false;
        flags.appendChild(el('p', 'rf-flags__lead', lead));
        var ul = el('ul', 'rf-flags__list');
        lines.forEach(function (s) { ul.appendChild(el('li', 'rf-flags__item', s)); });
        flags.appendChild(ul);
      } else {
        flags.hidden = true;
      }
    }

    var jb = $('[data-rf-jobs]', root);
    var list = $('[data-rf-jobs-list]', root);
    if (jb && list) {
      /* WHAT KIND OF RUG THIS ROOM NEEDS, READ OFF THE ROOM. An OUTDOOR space
       * still gets it: the tone, the colour and the pattern of a terrace are
       * all read exactly as a room's are, and the only thing that abstains is
       * the SCORE. Withholding the prescription too would be reporting one
       * deliberate abstention as two. */
      var jobs = rugNeeds(read, roomRead || { floorL: null });
      results.jobAxes = jobs.map(function (j) { return j.axis; });
      jb.hidden = !jobs.length;
      list.textContent = '';
      jobs.forEach(function (j) { list.appendChild(el('li', 'rf-job', j.text)); });
    }
  }

  function render() {
    var box = $('[data-fit-status]', root);
    if (box) box.hidden = true;
    /* THE BAR REACHES 100 AND THEN GOES, rather than sitting full above a
     * finished result. Its last floor is a real transition like every other:
     * the cards exist. */
    paintProgress();
    setTimeout(stopProgress, 350);
    paintRead();
    var out = $('[data-fit-results]', root);
    out.textContent = '';
    out.hidden = false;

    out.appendChild(readBack());
    if (!band || !band.ok) {
      out.appendChild(nothingFits());
      return;
    }
    out.appendChild(sizeLine());

    if (results.out.none) {
      out.appendChild(results.out.allVetoed ? allVetoedNotice() : nothingFits());
      return;
    }
    /* FEWER THAN TEN IS AN ANSWER, AND IT IS SAID BEFORE THE GRID RATHER THAN
     * LEFT TO BE COUNTED. The set used to be padded to length with rugs the
     * engine had already vetoed - so a page promising ten recommendations
     * delivered some it expected to disappear into the customer's floor. */
    if (results.out.short) {
      out.appendChild(el('p', 'fit-note',
        'Only ' + results.out.items.length + ' of the rugs we make suit this ' +
        'room at this size, so ' + results.out.items.length + ' is what this ' +
        'is. We would rather show you fewer than pad the list.'));
    }
    /* WHEN THE TEN ARE INDISTINGUISHABLE THE ORDER IS NOT A FINDING. Said once,
     * above the grid, instead of letting ten position labels imply a precision
     * six declared weights cannot support. */
    /* WHEN THE TEN ARE INDISTINGUISHABLE, THE NUMBERS STAY AND THE WORDING
     * CHANGES. `close` fires on the same quantity the pair is built from -
     * `spread < idx.close_spread` on the raw scores, which IS the after-number
     * spread divided by ten - so it is the engine telling us it cannot
     * distinguish these ten. Suppressing the pair would throw that away; saying
     * it plainly is what the note is for. */
    if (results.out.close) {
      out.appendChild(el('p', 'fit-note',
        'These ten are very close. The order, the colours and the numbers ' +
        'beside them are a guide rather than a verdict — any of these would ' +
        'suit the room.'));
    }
    var grid = el('div', 'fit-grid');
    /* WHICH OF A RUG'S TRUE CLAUSES THIS CARD SHOWS.
     *
     * The engine returns them strongest-first and that ordering is the
     * specification - it is what `_why` asserts and what the conformance test
     * holds both engines to. What it does NOT decide is which one a customer
     * reads, and taking the strongest every time produced a real page where all
     * ten cards said "Darker than your light wood floor..." verbatim: a
     * light-oak room where tone genuinely is the strongest term for every rug
     * that fits it. Ten identical sentences is the two-repeated-chips failure
     * this whole change removed, arriving in a new shape.
     *
     * So a card prefers a clause no earlier card has used. IT NEVER INVENTS ONE
     * AND NEVER BLANKS: every clause offered here was already gated on a fact
     * about THIS rug, so the second-strongest is as true as the first. A rug
     * whose clauses have all been used keeps its strongest - saying the same
     * true thing twice beats saying nothing. */
    /* ── TWO PASSES, TWO JOBS, AND ONLY ONE OF THEM IS THE COPY ────────────
     * `composeWhy` still runs and is still the WARNING path: a rug the engine
     * flags as likely to disappear into the customer's floor has one thing
     * worth saying and it is that. It is no longer the paragraph.
     *
     * `roomStories` writes what the room becomes, from the whole rug, spending
     * each observation once across the ten - see there. */
    var picks = composeWhy(results.out.items, results.jobAxes);
    var stories = roomStories(results.out.items, results.forEngine || null);
    for (var si = 0; si < picks.length; si++) {
      /* A WARNING OUTRANKS THE STORY. Telling somebody a rug will make their
       * room feel lived-in, directly above a line saying it may vanish into
       * their floor, is the page arguing with itself. */
      if (!picks[si] || picks[si].warn) continue;
      picks[si].sentences = stories[si] || [];
    }
    /* ONE LADDER PER RESULT SET, not one per card: it is a property of the ROOM
     * and the SET SIZE, so computing it inside `card()` would be the same
     * arithmetic ten times with ten chances to disagree. */
    /* THE WINDOW OF TEN, not the pool of twenty - see `shownItems`. The ladder
     * is sized on what is ON THE PAGE, so a rotated set still reads 9.8 down
     * to the floor rather than being cut out of a twenty-rung curve. */
    var visible = shownItems();
    var ladder = afterLadder(
      roomRead && roomRead.scored ? roomRead.score : null,
      visible.length);
    var built = visible.map(function (item, i) {
      var node = card(item, picks[i], ladder[i]);
      grid.appendChild(node);
      return node;
    });
    out.appendChild(grid);
    priceOrder(grid, built, ladder);
    /* THE CLOCK RESTARTS WITH THE NEW SET, which is what makes the fifteen
     * minutes mean "this set" rather than "this visit". */
    startSwap();

    if (results.out.quotaUnmet) {
      out.appendChild(el('p', 'fit-note', results.out.quotaNote));
    }
    out.appendChild(tapeCard());
    out.appendChild(footer());
  }

  /* ── AUTHORED STRINGS THE CLIENT NEEDS PER CARD ──────────────────────────
   * Four of them - the limited-time bar, the risk line and the two button
   * labels - are COPY, so they are section settings rendered once into hidden
   * source elements and cloned here. A literal in this file, or a value in
   * `RUG_FINDER_CONFIG`, would put a copy slot somewhere
   * `test_placeholder_markers` structurally cannot see.
   *
   * THE PLACEHOLDER FLAG IS CARRIED ACROSS with the text, so an unfilled slot
   * paints its red outline on every card - where a reviewer would actually
   * read it - rather than only on the hidden source nobody can see.
   *
   * AN EMPTY SOURCE RETURNS AN EMPTY, HIDDEN NODE rather than nothing, so a
   * caller never has to test before appending. */
  function srcText(sel) {
    var s = $(sel, root);
    return s ? s.textContent.trim() : '';
  }

  function cloneSrc(sel, cls, tag) {
    var s = $(sel, root);
    var e = el(tag || 'p', cls, s ? s.textContent.trim() : '');
    if (!s || !s.textContent.trim()) e.hidden = true;
    if (s && s.hasAttribute('data-rz-placeholder')) {
      e.setAttribute('data-rz-placeholder', '');
    }
    return e;
  }

  /* ── TEN CARDS, TEN OBSERVATIONS ────────────────────────────────────────
   * The engine hands each rug its AXES, strongest first, each carrying three
   * interchangeable parts: a LEAD about the room, a TEXT about this rug and a
   * RIDER for a second sentence. This turns them into paragraphs, and it lives
   * in the client for the reason the single clause did before it: only a pass
   * over the WHOLE set can see what the cards above already said.
   *
   * THE THING IT IS SOLVING IS THAT TEN CARDS SHARE ONE ROOM. "Your room reads
   * warm and busy" is true of every card, so a per-card template that opens
   * with it opens identically ten times - "one sentence with the adjectives
   * swapped". Three rules stop that:
   *
   *   1. A CARD PREFERS AN AXIS NO EARLIER CARD HAS LED ON, so the ten spread
   *      across tone, style, colour, accent and pattern rather than piling
   *      onto whichever term happens to be largest.
   *   2. THE ROOM OBSERVATION IS SPENT ONCE. The first card to lead on an axis
   *      says it; every later card on that axis opens on the rug instead, so
   *      the premise is stated once and never restated.
   *   3. THE RIDER COMES FROM A DIFFERENT AXIS, preferring one no earlier card
   *      has used - so two cards leading on the same axis still end
   *      differently.
   *
   * IT NEVER INVENTS AND NEVER PADS. Every part offered here was already gated
   * on a FACT about this rug by the engine, so the second-strongest axis is as
   * true as the first; and a rug with no axis at all gets the size sentence
   * alone, which is the instruction rather than a shortfall.
   */
  /* ── WHAT THE ROOM BECOMES ───────────────────────────────────────────────
   * By direct decision of the user (2026-09-07), replacing copy built from the
   * engine's ranking clauses.
   *
   * WHAT WAS WRONG WITH IT, IN THE USER'S WORDS: "Not 'this rug is blue and
   * your room is neutral, so it adds colour.' That's a fact about two objects.
   * I want the outcome - the room after, told so they can picture it."
   *
   * The clauses `composeWhy` assembles are the REASON THIS RUG RANKED - one
   * axis, one comparison, per sentence. They are true, they are why the engine
   * chose it, and they read as a scoring sheet because that is what they are.
   * They still do their job on the ranking side; they are no longer the copy.
   *
   * THESE SENTENCES ARE BUILT FROM THE WHOLE RUG, not one attribute: its
   * lightness against the room's own floor, its chroma, its warmth, how busy
   * it is, and what kind of pattern it carries. Every one of those is a
   * measured field on the index row - nothing here is a general rule about
   * rugs and nothing is invented about a product.
   *
   * TEN OBSERVATIONS, NOT ONE OBSERVATION TEN TIMES. Every sentence carries a
   * KEY, and a key is spent the first time it is used across the whole set. A
   * card that can only reach spent keys says LESS - one sentence instead of
   * two, or the character line alone - which is the instruction: "If two cards
   * would say the same thing a different way, say less on one of them." A
   * shorter card is honest; a card padded with a paraphrase of the card above
   * it is what made the old page read as one paragraph repeated.
   *
   * THE READING LEVEL IS THE PAGE'S. Short sentences, plain words, and a
   * concrete picture rather than an adjective - "the room stops feeling
   * empty", not "adds visual weight". */

  /* THE BANDS. Each is read off ONE measured field and each maps to a sentence
   * about the room afterwards. `null` where the field is missing: a rug we
   * cannot measure gets fewer sentences, never a guessed one. */
  function storyWeight(rugL, floorL) {
    if (typeof rugL !== 'number') return null;
    if (typeof floorL !== 'number') {
      return rugL < 45 ? { k: 'w-dark', s: 'It puts a deep, dark shape in the middle of the room. The room stops feeling empty.' }
           : rugL > 78 ? { k: 'w-light', s: 'It is soft and pale, so the room stays open and light.' }
           : null;
    }
    var d = rugL - floorL;
    if (d <= -30) return { k: 'w-anchor', s: 'Your floor stops floating. The room gets a solid base, and everything standing on it looks taller.' };
    if (d <= -18) return { k: 'w-weight', s: 'It drops real weight into the middle of the room. The space stops feeling like it is drifting.' };
    if (d <= -8)  return { k: 'w-settle', s: 'The middle of the room settles down. Things around it stop wandering.' };
    if (d >= 30)  return { k: 'w-open', s: 'The middle of the room opens right up. The space will look wider than it does now.' };
    if (d >= 18)  return { k: 'w-lift', s: 'It lifts the middle of the room and lets more light bounce around it.' };
    if (d >= 8)   return { k: 'w-lighten', s: 'It lightens the middle of the room without washing it out.' };
    return { k: 'w-tie', s: 'It sits between your floor and your furniture and ties the two together.' };
  }

  /* ── HOW DARK IT IS IN ITSELF, WHICH IS NOT THE SAME QUESTION ────────────
   * `storyWeight` compares the rug against THIS room's floor, so every rug in
   * a set lands in one or two bands - measured on a real ten-card result, all
   * ten sat between 18 and 33 points below the floor and shared two keys.
   * Their own lightness ran 38.6 to 52.3, a 14-point spread that a customer
   * can plainly see and that nothing was saying anything about.
   *
   * It is a real second fact rather than a restatement: "much darker than your
   * floor" and "nearly black in itself" are both true of one rug and describe
   * different things about it. */
  function storyDepth(L) {
    if (typeof L !== 'number') return null;
    if (L < 30) return { k: 'd-black',  s: 'It is close to black. In a bright room that is the strongest thing you can put on the floor.' };
    if (L < 42) return { k: 'd-deep',   s: 'The colour in it is deep and low. It reads as heavy in a good way, like a piece of furniture.' };
    if (L < 50) return { k: 'd-mid',    s: 'It is mid-toned, so it holds the room without turning it dark.' };
    if (L < 60) return { k: 'd-soft',   s: 'It is soft rather than dark. It marks the floor out without shutting the light down.' };
    if (L < 75) return { k: 'd-warmlt', s: 'It is light without being white, so it will not show every mark.' };
    return { k: 'd-pale', s: 'It is very pale. The room stays bright and the rug almost reads as part of the floor.' };
  }

  /* ── THE COLOUR IS TWO SEPARATE QUESTIONS AND THEY GET SEPARATE KEYS ──────
   * HOW MUCH colour (chroma) and WHICH colour (hue) are different facts about
   * a rug and they vary independently across a set. Measured on a real
   * ten-card result: chroma ran 3.9 to 12.2, so nine of the ten fell in one
   * band and every one of them wanted the same sentence - while the HUES were
   * blue, teal, purple, pink and neutral, five genuinely different things to
   * say about five genuinely different rugs.
   *
   * Keying them together is what left seven of ten cards with nothing. Colour
   * is also the most picturable thing about a rug: "the room feels sunnier" is
   * something a customer can see before the parcel arrives. */
  var HUE_STORY = {
    blue:   { k: 'h-blue',   s: 'It brings a cool blue into the room. The space feels calmer, and a little wider.' },
    teal:   { k: 'h-teal',   s: 'The green-blue in it is fresh. The room stops feeling shut in.' },
    green:  { k: 'h-green',  s: 'The green in it brings something living indoors. The room feels less like a box.' },
    purple: { k: 'h-purple', s: 'There is a soft purple in it. The room feels richer than it did, without trying hard.' },
    pink:   { k: 'h-pink',   s: 'The pink in it is warm and soft. The room feels kinder to walk into.' },
    red:    { k: 'h-red',    s: 'The red in it pulls the room in close. It will feel warmer on a cold evening.' },
    orange: { k: 'h-orange', s: 'The warm orange in it makes the room feel sunnier, even on a grey day.' },
    yellow: { k: 'h-yellow', s: 'The yellow in it lifts the whole room. The space feels brighter than the light in it.' }
  };

  function storyHue(hue, C) {
    /* A HUE ON A RUG WITH NO CHROMA IS NOT A COLOUR ANYBODY WILL SEE. The
     * index names a hue for every row including the near-grey ones, so this is
     * gated on the chroma actually being there. `neutral` has no entry by
     * design - "it is beige" is not a thing the room becomes. */
    if (typeof C === 'number' && C < 6) return null;
    return HUE_STORY[String(hue || '').toLowerCase()] || null;
  }

  function storyChroma(C, roomNeutral) {
    if (typeof C !== 'number') return null;
    if (C >= 22) return { k: 'c-strong', s: 'This is the one thing in the room with real colour in it. Everything else settles around it.' };
    if (C >= 10) return roomNeutral
      ? { k: 'c-mid-neutralroom', s: 'Colour comes into a room that has none. You notice it without it shouting.' }
      : { k: 'c-mid', s: 'There is enough colour in it to give the room a mood of its own.' };
    if (C >= 6) return { k: 'c-soft', s: 'The colour in it is faint. It warms the room a little and then stays out of the way.' };
    return { k: 'c-none', s: 'It brings no new colour, so the room stays exactly as quiet as it is.' };
  }

  function storySurface(busy) {
    if (typeof busy !== 'number') return null;
    if (busy >= 0.9)  return { k: 's-dense', s: 'It is packed with detail. Stand over it and you will still be finding things in it next year.' };
    if (busy >= 0.7)  return { k: 's-busy', s: 'There is a lot to look at close up. Your eye keeps finding new things in it.' };
    if (busy >= 0.35) return { k: 's-some', s: 'There is just enough going on in it to hold your eye for a moment.' };
    if (busy >= 0.12) return { k: 's-light', s: 'The pattern is light. It gives the room something to look at and then gets out of the way.' };
    return { k: 's-plain', s: 'It is plain and soft. The room gets calmer, not busier.' };
  }

  /* THE CHARACTER LINE. Keyed on the sub-type, which is the one field that
   * says what KIND of rug this is rather than how much of something it has.
   *
   * ALL 21 SUB-TYPES IN THE LIVE INDEX ARE COVERED. The first version carried
   * the Crea names and four of ours, so `speckled_mosaic` (62 rows),
   * `overlapping_circles` (22) and eight more fell through and said nothing -
   * which is how a card ends up with no paragraph. A sub-type the table does
   * not carry is still SKIPPED rather than guessed at: an invented character
   * sentence is the one thing here that could be untrue about a product. */
  var STORY_CHARACTER = {
    'persian':                 { k: 'x-old',    s: 'It looks old and hand-made. The room will feel like it has been here for years.' },
    'bold_oriental':           { k: 'x-old',    s: 'It looks old and hand-made. The room will feel like it has been here for years.' },
    'central_medallion':       { k: 'x-centre', s: 'It has one big shape in the middle, so the room finally has a centre.' },
    'faded_medallion':         { k: 'x-faded',  s: 'The big shape in the middle has faded back. The room gets a centre without being told where to look.' },
    'moroccan':                { k: 'x-easy',   s: 'The lines in it are loose and easy. The room feels less serious.' },
    'geometric':               { k: 'x-order',  s: 'The shapes in it are neat and even. A room that felt untidy looks sorted out around it.' },
    'chequered':               { k: 'x-check',  s: 'The squares in it run straight and even. The room reads as tidier than it is.' },
    'floral':                  { k: 'x-soft',   s: 'It is covered in soft, growing shapes. The room feels gentler.' },
    'all_over_floral':         { k: 'x-soft',   s: 'It is covered in soft, growing shapes. The room feels gentler.' },
    'striped':                 { k: 'x-long',   s: 'The lines run one way and pull your eye along them, so the room reads longer.' },
    'brushstroke_bands':       { k: 'x-brush',  s: 'Wide painted bands run across it. The room gets movement without getting busy.' },
    'damask':                  { k: 'x-dress',  s: 'It is dressed up rather than plain. The room feels a little smarter.' },
    'abstract':                { k: 'x-free',   s: 'Nothing in it repeats exactly. The room feels less arranged and more lived in.' },
    'distressed_watercolor':   { k: 'x-worn',   s: 'It looks gently worn already, so nothing else in the room has to look new.' },
    'single_color_distressed': { k: 'x-worn2',  s: 'One colour, softly rubbed back. The room gets age without getting fussy.' },
    'single_color_overdyed':   { k: 'x-dyed',   s: 'One deep colour, dyed right through. The room gets a floor with a mood.' },
    'speckled_mosaic':         { k: 'x-speck',  s: 'It is made of thousands of tiny flecks. From standing height it reads as one colour, and up close it is alive.' },
    'overlapping_circles':     { k: 'x-circles',s: 'Soft circles drift across each other. The room feels calmer and less square.' },
    'minimalist_border':       { k: 'x-border', s: 'One quiet line runs round the edge. The room gets a shape to sit inside.' },
    'patchwork':               { k: 'x-mix',    s: 'It is made of many small pieces, so the room can hold more than one colour happily.' },
    'solid colour':            { k: 'x-quiet',  s: 'It is one colour all the way across. The room gets a clean, quiet floor.' }
  };

  function storyCharacter(sub) {
    var k = String(sub || '').toLowerCase().replace(/\s+/g, ' ');
    return STORY_CHARACTER[k] || null;
  }

  /* THE ORDER IS THE ORDER OF THE ARGUMENT: what the room gains, what colour
   * it gains, what kind of thing this is, and what it is like to look at.
   * `roomStories` assigns them across the whole set so no sentence is spent
   * twice, and a card that runs out of unspent sentences is left shorter
   * rather than padded. */
  var STORY_MAX = 2;

  function roomStories(items, room) {
    var floorL = room && typeof room.floorL === 'number' ? room.floorL : null;
    var neutral = !!(room && room.isNeutral);
    var spent = {};
    return items.map(function (item) {
      var p = item.product || {};
      var cands = [
        storyWeight(p.L, floorL),
        storyHue(p.hue, p.C),
        storyCharacter(p.sub),
        storyDepth(p.L),
        storyChroma(p.C, neutral),
        storySurface(p.busy)
      ].filter(Boolean);

      var out = [];
      for (var i = 0; i < cands.length && out.length < STORY_MAX; i++) {
        if (spent[cands[i].k]) continue;
        spent[cands[i].k] = 1;
        out.push(cands[i].s);
      }
      /* SAY LESS RATHER THAN SAY IT AGAIN, and that includes saying nothing:
       * a card whose every observation has already been made on a card above
       * it prints no paragraph and the size clause stands alone. The rug is
       * still there, still ranked, still priced - what it loses is a sentence
       * that would have been a paraphrase of one the customer has just read.
       *
       * FIVE AXES RATHER THAN FOUR IS WHAT MAKES THAT RARE. Measured on a real
       * all-AI set of ten - the hardest case, because those rugs are close to
       * each other on every scored axis - four axes left SEVEN cards silent.
       * The fix was not to loosen the rule but to give it more true things to
       * choose from. */
      return out;
    });
  }

  /* ── THE TEN ARE ORDERED MOST EXPENSIVE FIRST ────────────────────────────
   * By direct decision of the user (2026-09-07), and it replaces fit order as
   * the ORDER of the page. Fit still decides MEMBERSHIP - the ten on the page
   * are the ten the engine chose, and a vetoed rug never reaches here - so
   * every card being sorted is a rug we are prepared to recommend.
   *
   * ── THE EQUAL-PRICE RULE, STATED RATHER THAN LEFT TO FALL OUT ────────────
   * "When every price is equal, order by fit - best rug first, ignore price
   * entirely. Make this an explicit rule, not a silent fallback."
   *
   * It is not a corner case: EVERY AI-Generated rug prices at area times a
   * flat rate, so a set drawn entirely from our own line at one size is ONE
   * PRICE. Measured over 1,575 simulated result sets on the live index,
   * **43.4% are entirely AI-Generated** - so on nearly half of all rooms there
   * is nothing to sort on, and a comparator left to its own devices would
   * shuffle ten identical prices on whatever its tie-break happened to be.
   * `allOnePrice()` answers the question out loud and returns the fit order
   * untouched.
   *
   * ── WHICH PRICE IT SORTS ON, AND WHY THE PAGE SETTLES ────────────────────
   * "Do not hold cards back for the ten variant lookups. Render on the
   * from-price." So the cards are appended immediately and this runs twice:
   * once now on the index's FROM-price, and once when the exact prices land.
   *
   * THE SECOND PASS IS NOT OPTIONAL, AND THE NUMBER IS WHY. The from-price is
   * the cheapest size in that product's own pool, and the pools differ - so it
   * is not a rank of what the cards CHARGE. Measured against the workbook
   * Shopify is loaded from, across 741,111 product pairs at six real sizes:
   * **7.78% of pairs come out in the opposite order**. Simulated as pages of
   * ten, that is **85% of pages showing at least one card priced HIGHER than
   * the card above it** - a page contradicting its own heading. So the order
   * is corrected when the true prices arrive.
   *
   * IT COSTS NO EXTRA REPAINT. Those prices land in a repaint that already
   * happens - the card goes from "from $X" to the exact figure with its
   * discount and struck original - so the order settles in the same frame the
   * numbers do, about 286ms after the cards appear (measured, ten lookups in
   * parallel, cold). Nothing is held back and nothing is painted twice.
   *
   * ── THE CURVE FOLLOWS THE ORDER (option A, direct decision) ──────────────
   * `ladder[i]` is re-attached to whatever card is now at position i, so the
   * column still reads 9.8 down to the floor. THE CONSEQUENCE IS RECORDED IN
   * THE AMENDMENTS AND MUST NOT BE SOFTENED HERE: the number now follows PRICE
   * POSITION. It was already a rank curve rather than a measurement of fit;
   * what changed is which rank it follows. Do not describe it as a measurement
   * of anything, in copy or in a comment. */

  /* ONE DEFINITION OF THE THREE TIER SENTENCES. `card()` writes them when the
   * card is built and `priceOrder` rewrites them when the card moves, so two
   * copies is how the page would come to say two different things about one
   * position. Extracted the moment the second caller existed.
   *
   * THE SENTENCE COUNTS THE REAL SET. 16.7% of runs return fewer than ten, and
   * "lowest of your ten" on a page showing six is simply false. */
  /* ── THE DETAILS POPUP ───────────────────────────────────────────────────
   * By direct decision of the user (2026-09-07). Slot 467's button used to
   * link to the product page: "They go and they don't come back - the flow is
   * lost." Nobody leaves the page now.
   *
   * IT REUSES THE VISUALISER'S SHELL rather than being a second modal -
   * `window.RugModal`, in rug-visualiser.js, which is loaded on this page
   * already because the finder calls `RugVisualiser.open`. The X, Escape and
   * click-outside are all that shell's. Nothing here re-implements closing.
   *
   * THE FLOW SURVIVES BECAUSE NOTHING NAVIGATES. `showModal()` puts an overlay
   * over the results; the grid underneath is never unmounted or re-rendered,
   * so closing returns the customer to the same card in the same list.
   *
   * IT COSTS NO REQUEST. The images and the description are cached by
   * `resolveVariant`, which already runs for the price and the Loox id. */

  /* ── THE WITHHELD FLAT RUNNER HERO MUST NOT REACH THIS GALLERY ────────────
   * `src/shared/artefact_policy.py` is the ONE declaration and this is the
   * FIFTH surface to consult it - after the shape-media snippet, the gallery
   * content snippet, product-information.liquid and rug-product-image.liquid.
   * The marker is spelled here with no import between the two, exactly as it
   * is in those four, and `test_artefact_policy.py` asserts every file that
   * mentions it agrees with the module. Add to that list, never past it. */
  var UNLISTED_MARKER = 'UNLISTED |';

  /* THE SHAPE THIS CARD IS RECOMMENDING. A rectangle card showing runner
   * mockups is the same fault as a runner SKU carrying rectangle images, one
   * surface further out - "a SKU's images MUST match its shape". `SHARED`
   * media (the two benefit panels) belongs to both and is kept. */
  function detailMedia(media, shape) {
    var want = String(shape || '').toUpperCase();
    return (media || []).filter(function (m) {
      var a = m.alt.toUpperCase();
      if (a.indexOf(UNLISTED_MARKER) !== -1) return false;
      if (!want) return true;
      /* An alt with no shape token at all is kept: it is somebody's photograph
       * that predates the convention, and hiding a real product image because
       * a label is missing is the worse error. */
      if (a.indexOf('RECTANGLE') === -1 && a.indexOf('RUNNER') === -1) return true;
      return a.indexOf(want) !== -1 || a.indexOf('SHARED') !== -1;
    }).sort(function (a, b) { return a.pos - b.pos; });
  }

  /* ── THE FEATURES ARE THE PRODUCT'S OWN, AND THE INDEX CARRIES THEM ───────
   * `p.ft` is built by `build_index.py` from the workbook's own columns -
   * machine_washable, non_slip_backing, antibacterial, antiallergenic,
   * pet_friendly - and only where the cell says Yes. So a woven rug has no
   * `w` and shows no washable symbol, which is the whole rule: that claim is
   * true of the 464 digital prints and false of the 887 woven rows.
   *
   * NOTHING IS DERIVED FROM `src` HERE. The two would render identically
   * today and would be a second implementation of a rule the catalogue owns.
   *
   * A CODE THE TABLE DOES NOT CARRY IS SKIPPED. A symbol we cannot label is a
   * picture with no meaning, and inventing a label for an unknown code is how
   * a claim nobody checked reaches a customer. */
  var FEATURE_LABEL = {
    w:  { label: 'Machine washable', d: 'M4 7h16v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V7zm3 0V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2M8 12a4 4 0 0 0 8 0' },
    ns: { label: 'Non-slip backing', d: 'M4 16h16M6 16V9a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v7M8 19l1.5-3M16 19l-1.5-3M12 19v-3' },
    ab: { label: 'Antibacterial',    d: 'M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-3zM9.5 12.5l1.8 1.8 3.4-3.6' },
    aa: { label: 'Antiallergenic',   d: 'M12 4c3 3 5 5.5 5 8a5 5 0 0 1-10 0c0-2.5 2-5 5-8zM5 19h14' },
    pf: { label: 'Pet friendly',     d: 'M8.5 9a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2zm7 0a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2zM5.5 13.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm13 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM12 12c2.5 0 4.5 2.4 4.5 4.3 0 1.5-1.2 2.2-2.6 2.2h-3.8c-1.4 0-2.6-.7-2.6-2.2C7.5 14.4 9.5 12 12 12z' }
  };

  function featureNodes(ft) {
    var out = [];
    (ft || []).forEach(function (code) {
      var f = FEATURE_LABEL[code];
      if (!f) return;                       /* unknown code: no symbol, no label */
      var li = el('li', 'rf-det__feat');
      var ic = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      ic.setAttribute('viewBox', '0 0 24 24');
      ic.setAttribute('aria-hidden', 'true');
      ic.setAttribute('class', 'rf-det__icon');
      var pa = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pa.setAttribute('d', f.d);
      pa.setAttribute('fill', 'none');
      pa.setAttribute('stroke', 'currentColor');
      pa.setAttribute('stroke-width', '1.6');
      pa.setAttribute('stroke-linecap', 'round');
      pa.setAttribute('stroke-linejoin', 'round');
      ic.appendChild(pa);
      li.appendChild(ic);
      li.appendChild(el('span', 'rf-det__featlab', f.label));
      out.push(li);
    });
    return out;
  }

  /* ── THE DESCRIPTION IS A VALUE STATEMENT, NOT A PRODUCT DESCRIPTION ─────
   * By direct decision of the user (2026-09-07): "Fifty words maximum. Rewrite
   * it rather than truncating - the current one is a product description and I
   * want a value statement. What the rug does for the room, not what it's made
   * of."
   *
   * IT IS NOT SHOPIFY'S. That field is ~1,100 words of catalogue copy about
   * fibre, weave and pile, written for a product page, and truncating it to
   * fifty words leaves a sentence cut in half about a material.
   *
   * IT IS THE CARD'S OWN STORY GENERATOR, run for ONE rug. `roomStories`
   * already turns this rug's measured axes - its lightness against this
   * room's floor, its hue, its pattern type, its depth, its chroma, its
   * busyness - into sentences about what the room becomes. That is exactly a
   * value statement, it is third-grade by construction, and it costs nothing.
   *
   * WITHOUT THE SET-WIDE DEDUPE, deliberately. On a card a sentence is spent
   * once across the ten so the page reads as ten observations; in the popup
   * there is one rug and the customer wants its strongest three, not what was
   * left over.
   *
   * FIFTY WORDS IS ENFORCED BY DROPPING WHOLE SENTENCES, never by cutting one.
   * A value statement that stops mid-clause is worse than a shorter one. */
  var DETAIL_WORDS = 50;

  function rugValue(p, room) {
    var sents = roomStories([{ product: p }], room)[0] || [];
    var out = [], n = 0;
    for (var i = 0; i < sents.length; i++) {
      var w = sents[i].split(/\s+/).length;
      if (n + w > DETAIL_WORDS) break;
      out.push(sents[i]); n += w;
    }
    return out.join(' ');
  }

  /* ── EXACTLY THREE FEATURES, AND THE ORDER IS RARITY FIRST ───────────────
   * By direct decision: "Exactly three. Not four, not five. Pick the three
   * strongest for that product from what the catalogue holds."
   *
   * HOW "STRONGEST" IS DECIDED, stated rather than left to the order the index
   * happens to emit: THE RAREST TRUE THING IS THE MOST INFORMATIVE. Measured
   * across the live index, `w` (machine washable) is on 464 of 1,351 rows and
   * the other four are on all of them - so washable leads wherever it is true,
   * because it is the only one that distinguishes this rug from most of the
   * catalogue. The four universal ones cannot be ranked by rarity, so their
   * order is DECLARED here and is a judgement about what a rug buyer acts on:
   * a floor that does not slide, then a home with animals in it, then the two
   * hygiene claims.
   *
   * FEWER THAN THREE SHOWS WHAT THERE IS. Nothing is padded and nothing is
   * borrowed from a neighbouring product - a symbol we cannot earn is a claim
   * about a physical rug. No row in the live index is below four today; the
   * rule exists for the row that will be. */
  var FEATURE_ORDER = ['w', 'ns', 'pf', 'ab', 'aa'];
  var DETAIL_FEATURES = 3;

  function topFeatures(ft) {
    var have = {}, out = [];
    (ft || []).forEach(function (c) { have[c] = 1; });
    for (var i = 0; i < FEATURE_ORDER.length && out.length < DETAIL_FEATURES; i++) {
      if (have[FEATURE_ORDER[i]] && FEATURE_LABEL[FEATURE_ORDER[i]]) {
        out.push(FEATURE_ORDER[i]);
      }
    }
    return out;
  }

  function openDetails(p) {
    var dlg = $('[data-rf-details]', root);
    if (!dlg || !window.RugModal) return false;
    var cache = VARIANTS[p.h] || {};

    /* THE NAME ON THE CARD, NOT THE SHOPIFY TITLE. The customer clicked a card
     * that said "Upland"; opening a panel headed "Upland Cream Grey Modern
     * Geometric Machine Woven Entryway Rug" makes them check they pressed the
     * right thing. The full title is a search string, and what it carries -
     * the colour, the pattern, the construction - the description below says
     * in sentences and the symbols say as symbols. */
    /* THE SAME NAME THE CARD CARRIES. The popup only ever opens for a product
     * whose lookup has already landed - it is what supplies the gallery and the
     * description below - so the title and type are in hand. */
    $('[data-rf-det-name]', dlg).textContent = shortName(p, cache);

    /* PLAIN TEXT, ALWAYS. Shopify's `/products/<h>.js` returns `description`
     * with no markup on this catalogue - measured across it - and `textContent`
     * is what keeps that true whatever a future description contains. */
    var d = $('[data-rf-det-desc]', dlg);
    var value = rugValue(p, (results && results.forEngine) || null);
    d.textContent = value;
    d.hidden = !value;

    /* THE LOOX RATING, THE SAME WIDGET THE CARD CARRIES. It is a second
     * `.loox-rating` for the same product, and `RugLoox` keys its per-view
     * memo on the PRODUCT ID rather than on the element - so a widget built
     * after the cards have already been answered for is filled the moment it
     * exists and the popup costs no extra request. */
    var st = $('[data-rf-det-stars]', dlg);
    st.textContent = '';
    var sw = looxStars(p.h);
    if (sw) st.appendChild(sw);
    st.hidden = !sw;

    var feats = $('[data-rf-det-feats]', dlg);
    feats.textContent = '';
    var nodes = featureNodes(topFeatures(p.ft));
    nodes.forEach(function (n) { feats.appendChild(n); });
    /* NO FEATURES, NO LIST AND NO RULE ABOVE IT. A product the index knows
     * nothing about shows its pictures and its value statement and makes no
     * claims at all - and a dividing line above nothing is a line to nowhere. */
    feats.hidden = !nodes.length;
    var rule = dlg.querySelector('.rf-det__rule');
    if (rule) rule.hidden = !nodes.length;

    var rail = $('[data-rf-det-rail]', dlg);
    var nav = $('[data-rf-det-nav]', dlg);
    rail.textContent = ''; nav.textContent = '';
    var shots = detailMedia(cache.media, p.sh);
    if (!shots.length && p.img) shots = [{ src: p.img, alt: p.n || '', pos: 1 }];
    shots.forEach(function (m, i) {
      var fig = el('div', 'rf-det__slide');
      var im = document.createElement('img');
      im.loading = i === 0 ? 'eager' : 'lazy';
      im.decoding = 'async';
      im.src = m.src;
      /* THE ROUTING TOKEN NEVER REACHES A SCREEN READER. The alt is
       * `RECTANGLE | HERO | Willow rectangle`; only the human half is read. */
      im.alt = m.alt.split('|').pop().trim() || (p.n || 'This rug');
      fig.appendChild(im);
      rail.appendChild(fig);
    });
    detailNav(rail, nav, shots.length);
    window.RugModal.open(dlg);
    return true;
  }

  /* THE GALLERY IS THE PAGE'S OWN RAIL: scroll-snap, `[data-rz-drag]`, dots
   * from `scrollLeft`. Same conventions as the example pair and the reviews,
   * and the fourth surface on the theme's one drag module rather than the
   * fourth to invent a swiper. It does NOT auto-advance - a gallery somebody
   * opened to look at must not move while they are looking. */
  function detailNav(rail, nav, n) {
    if (n < 2) return;
    var prev = el('button', 'rf-det__arrow rf-det__arrow--prev', '\u2039');
    var next = el('button', 'rf-det__arrow rf-det__arrow--next', '\u203A');
    prev.type = next.type = 'button';
    prev.setAttribute('aria-label', 'Previous photograph');
    next.setAttribute('aria-label', 'Next photograph');
    var dots = el('span', 'rf-det__dots'), dot = [];
    for (var i = 0; i < n; i++) {
      var b = el('button', 'rf-det__dot');
      b.type = 'button';
      b.setAttribute('aria-label', 'Photograph ' + (i + 1) + ' of ' + n);
      (function (k) { b.addEventListener('click', function () { to(k); }); })(i);
      dots.appendChild(b); dot.push(b);
    }
    nav.appendChild(prev); nav.appendChild(dots); nav.appendChild(next);
    function at() {
      var w = rail.clientWidth || 1;
      return Math.max(0, Math.min(n - 1, Math.round(rail.scrollLeft / w)));
    }
    function paint() {
      var i = at();
      for (var k = 0; k < dot.length; k++) dot[k].classList.toggle('is-on', k === i);
      prev.disabled = i === 0; next.disabled = i === n - 1;
    }
    function to(i) {
      rail.scrollTo({ left: Math.max(0, Math.min(n - 1, i)) * rail.clientWidth,
                      behavior: 'smooth' });
    }
    prev.addEventListener('click', function () { to(at() - 1); });
    next.addEventListener('click', function () { to(at() + 1); });
    var t = null;
    rail.addEventListener('scroll', function () {
      if (t) return;
      t = setTimeout(function () { t = null; paint(); }, 90);
    });
    paint();
  }

  /* ── THE COUNTDOWN, AND WHAT IT IS HONESTLY COUNTING DOWN TO ─────────────
   * By direct decision of the user (2026-09-07): fifteen minutes, and at zero
   * the ten rugs are replaced with a different ten.
   *
   * THE WORDING HAD TO FOLLOW WHAT HAPPENS, AND THIS IS WHY. Slot 465 was
   * "This offer ends in", and no offer ends: the 50% is the standing pricing
   * rule (`list = sale / (1 - discount)`) and the SALE badge is on every
   * product card sitewide. A countdown beside a discount that does not expire
   * is a false statement with a clock on it - the exact thing voice.md's ban
   * on invented urgency is about, and worse than the static bar it replaces
   * because a number ticking down asserts a deadline rather than implying one.
   *
   * WHAT DOES EXPIRE IS THE SELECTION, and that is real: at zero this set is
   * genuinely gone and a different ten is on the page. So the label names the
   * SET. It is still manufactured - WE choose to swap them, nothing external
   * forces it - which is why it is recorded in the amendments as a dated
   * exception exactly as the bar was. What it is not is untrue.
   *
   * A RUG IN THE CART IS NEVER SWAPPED OUT. A customer who added Bellcote and
   * then watched the page replace it has lost the thing they were buying, and
   * "it is still in your cart" is no help to somebody looking at the screen
   * for it. Kept cards hold their place; the rest rotate around them.
   *
   * IT ROTATES RATHER THAN RUNNING OUT. Twenty rugs, ten at a time, wrapping -
   * so the second expiry returns the first ten. "These ten change" stays true
   * on every tick; "you will never see these again" was never claimed. */
  var SWAP_MS = 15 * 60 * 1000;
  var swapAt = 0, swapTick = null;

  /* THE HANDLES A CUSTOMER HAS ACTUALLY ADDED. Recorded at the moment the
   * cart accepts the line, not when the button is pressed - a failed add must
   * not pin a card. */
  var inCart = {};

  function keptAndRotated(items, offset) {
    var kept = [], rest = [];
    items.forEach(function (it) {
      var h = it.product && it.product.h;
      if (h && inCart[h]) kept.push(it); else rest.push(it);
    });
    /* THE KEPT ONES FIRST, THEN A WINDOW OF THE REST, WRAPPING. A customer
     * with three rugs in the cart sees those three plus seven others, so the
     * page is always ten and always contains everything they are buying. */
    var want = RESULTS - kept.length;
    var out = kept.slice(0, RESULTS);
    if (want > 0 && rest.length) {
      for (var i = 0; i < want; i++) out.push(rest[(offset + i) % rest.length]);
    }
    return out;
  }

  function shownItems() {
    var all = (results && results.out && results.out.items) || [];
    if (all.length <= RESULTS) return all;
    return keptAndRotated(all, (state.tenOffset || 0) * RESULTS);
  }

  function stopSwap() {
    if (swapTick) { clearInterval(swapTick); swapTick = null; }
  }

  function startSwap() {
    stopSwap();
    var all = (results && results.out && results.out.items) || [];
    /* NOTHING TO SWAP TO, NO CLOCK. A room that returned eleven rugs would
     * show a countdown that changes almost nothing, and a room that returned
     * ten would show one that changes nothing at all - a promise the page
     * cannot keep. 16.7% of runs return fewer than ten to begin with. */
    if (all.length <= RESULTS) { paintSwap(null); return; }
    swapAt = Date.now() + SWAP_MS;
    paintSwap(swapAt - Date.now());
    swapTick = setInterval(function () {
      var left = swapAt - Date.now();
      if (left > 0) { paintSwap(left); return; }
      stopSwap();
      state.tenOffset = (state.tenOffset || 0) + 1;
      render();                       /* rebuilds the grid from the new window */
    }, 1000);
  }

  function paintSwap(msLeft) {
    var nodes = root.querySelectorAll('[data-rf-countdown]');
    for (var i = 0; i < nodes.length; i++) {
      if (msLeft === null) { nodes[i].hidden = true; continue; }
      var s = Math.max(0, Math.floor(msLeft / 1000));
      nodes[i].hidden = false;
      nodes[i].textContent = Math.floor(s / 60) + ':' +
                             (s % 60 < 10 ? '0' : '') + (s % 60);
    }
  }

  function tierSentence(tier, setSize) {
    if (tier === 'low') return 'Lowest of your ' + (setSize || 10);
    return tier === 'best' ? 'Best match for your room' : 'Also fits your room';
  }

  function cardPrice(node) {
    /* THE EXACT PRICE IF THE LOOKUP HAS LANDED, THE INDEX'S FROM-PRICE UNTIL
     * THEN. `paintSale` marks the line `is-exact` at the moment it becomes the
     * number the button will charge, so that class is the one honest signal
     * for which of the two we are holding. */
    var line = node.querySelector('.fit-card__price');
    if (line && line.classList.contains('is-exact')) {
      var now = line.querySelector('.fit-card__now');
      var v = now ? Number(String(now.textContent).replace(/[^0-9.]/g, '')) : NaN;
      if (!isNaN(v)) return { v: v, exact: true };
    }
    var px = Number(node.getAttribute('data-from-price'));
    return { v: isNaN(px) ? 0 : px, exact: false };
  }

  function allOnePrice(vals) {
    for (var i = 1; i < vals.length; i++) {
      /* A CENT IS THE UNIT THE PAGE PRINTS IN, so two prices that render
       * identically are equal here whatever the float says. */
      if (Math.abs(vals[i] - vals[0]) >= 0.005) return false;
    }
    return true;
  }

  function priceOrder(grid, built, ladder) {
    if (!grid || built.length < 2) return;

    function apply() {
      var rows = built.map(function (node, i) {
        var pr = cardPrice(node);
        return { node: node, v: pr.v, exact: pr.exact, fit: i };
      });

      /* THE EXPLICIT RULE, FIRST AND BY NAME. */
      var order;
      if (allOnePrice(rows.map(function (r) { return r.v; }))) {
        order = rows.slice();                 /* fit order, price ignored */
        grid.setAttribute('data-order', 'fit-all-one-price');
      } else {
        order = rows.slice().sort(function (a, b) {
          var d = b.v - a.v;
          /* TIES GO TO FIT, which is the only other thing the page knows and
           * the same rule the equal-price case applies to the whole set. */
          return Math.abs(d) >= 0.005 ? d : a.fit - b.fit;
        });
        grid.setAttribute('data-order', 'price-desc');
      }

      /* MOVE ONLY WHAT MOVED. Re-appending every card would restart every
       * image decode and every Loox widget on a page that is already settled. */
      var changed = false;
      for (var i = 0; i < order.length; i++) {
        if (grid.children[i] !== order[i].node) { changed = true; break; }
      }
      if (changed) order.forEach(function (r) { grid.appendChild(r.node); });

      /* ── EVERYTHING THAT DESCRIBES POSITION FOLLOWS THE POSITION ─────────
       * The curve (option A, direct decision), the rank badge, AND the tier
       * band. Re-attached every pass, so a card that moves takes its new
       * position's number rather than the one it was built with.
       *
       * THE TIER HAD TO MOVE WITH THEM OR THE CARD ARGUES WITH ITSELF. It is
       * three things at once - a green/yellow/red frame, a rank badge and a
       * sentence - and leaving it on the FIT rank while the number and the
       * badge follow price put "1", "9.8" and "Lowest of your ten" on one
       * card. `card()`'s own note already refuses that shape: "the label may
       * not make a quality claim the colour would then contradict".
       *
       * THE DISTRIBUTION IS `match.tier_of`'s, UNCHANGED: rank 1 is best, the
       * bottom `floor(n x tier_low_fraction)` are low, the rest strong. Only
       * the rank fed into it changed. */
      var setSize = order.length;
      var lowFrom = setSize - Math.floor(setSize * (idx.tier_low_fraction || 0.2));
      order.forEach(function (r, i) {
        var n = r.node.querySelector('.fit-pair__num[data-rf-after]');
        if (n && ladder[i] !== undefined) n.textContent = ladder[i].toFixed(1);
        var rk = r.node.querySelector('.fit-card__rank');
        if (rk) rk.textContent = String(i + 1);

        var pos = i + 1;
        var t = pos <= (idx.tier_best_ranks || 1) ? 'best'
              : pos > lowFrom ? 'low' : 'strong';
        r.node.className = 'fit-card fit-card--' + t;
        var lab = r.node.querySelector('.fit-card__tier');
        if (lab) {
          lab.className = 'fit-card__tier fit-card__tier--' + t;
          /* THE WORDS ARE THE AUTHORED ONES AND ARE NOT REWRITTEN HERE. They
           * still read as claims about FIT ("Best match for your room") on a
           * page now ordered by PRICE, and that is a copy decision rather than
           * a rendering one - flagged rather than quietly reworded. */
          var txt = tierSentence(t, setSize);
          var sr = lab.querySelector('.visually-hidden');
          lab.textContent = txt;
          if (sr) {
            sr.textContent = 'Ranked ' + pos + ' of ' + setSize + '. ';
            lab.insertBefore(sr, lab.firstChild);
          }
        }
      });
      return rows.every(function (r) { return r.exact; });
    }

    apply();                                  /* now, on the from-price */

    /* AND AGAIN AS THE TRUE PRICES ARRIVE. Polled rather than driven by a
     * callback because ten independent lookups land at ten different moments
     * and the page is only correct once the LAST one has; it stops the moment
     * every card is exact, and gives up after a bounded wait so a product
     * whose lookup never answers cannot leave a timer running for the visit. */
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (apply() || tries > 40) clearInterval(iv);   /* 40 x 150ms = 6s */
    }, 150);
  }

  function composeWhy(items, spentAxes) {
    var ledCount = {}, saidLead = {}, usedRider = {}, usedSaid = {}, i;
    /* ── THE JOB BLOCK ON SCREEN 4 HAS ALREADY SAID THESE ──────────────────
     * The room observation is spent ONCE - that is this function's whole
     * design, and the flow made it a question across two screens rather than
     * down one page. A lead printed under the score and again on the first
     * card of that axis is the same sentence twice, one press apart.
     *
     * SEEDING `saidLead` IS THE WHOLE CHANGE. Every card on a spent axis takes
     * the branch that opens on the RUG instead, which is what the reader wants
     * anyway: screen 4 has just told them their floor is pale, and the card
     * answers it with "This one is much darker than the floor". */
    (spentAxes || []).forEach(function (a) { saidLead[a] = true; });
    return items.map(function (item) {
      var clauses = item.why || [];
      /* A WARNING IS RETURNED ALONE BY THE ENGINE and is passed through whole.
       * A rug that may vanish into the customer's floor has nothing else worth
       * saying about it, and pairing that with a compliment would be worse
       * than either sentence on its own. */
      if (clauses.length && clauses[0].kind === 'warn') {
        return { sentences: [clauses[0].text], warn: true };
      }
      /* ONLY A CLAUSE WITH A `text` CAN LEAD. Chroma has a rider and nothing
       * else, by construction in the engine: it is not one of the three scored
       * terms, so a card may not open by offering it as the reason it ranked. */
      var leadable = clauses.filter(function (c) { return c.text; });
      if (!leadable.length) return { sentences: [], warn: false };

      /* THE LEAST-USED AXIS WINS, NOT SIMPLY AN UNUSED ONE. Preferring the
       * first unused axis fixed the first five cards and did nothing for the
       * rest: once every axis had led once, every later card fell back to its
       * STRONGEST, which is `style` at v = 1.0 for most rugs - so five of ten
       * cards read "This one is modern too." That is the defect this whole
       * change exists to remove, surviving inside the fix for it.
       *
       * Counting spreads ten cards over five axes roughly two each. Ties keep
       * the ENGINE'S order, which is strongest-first, so within an axis budget
       * the card still leads on the term that did the most work.
       *
       * THE TRADE, STATED: a card may open on its SECOND-strongest axis rather
       * than its first. Every axis offered here was gated on a FACT about this
       * rug, so nothing said is less true - what is given up is that the
       * opening sentence is no longer always the largest term. A page of ten
       * identical sentences explains nothing at all, which is worse. */
      var primary = leadable[0], bestN = ledCount[primary.axis] || 0;
      for (i = 1; i < leadable.length; i++) {
        var n = ledCount[leadable[i].axis] || 0;
        if (n < bestN) { primary = leadable[i]; bestN = n; }
      }
      var times = ledCount[primary.axis] || 0;
      ledCount[primary.axis] = times + 1;

      var sentences = [];
      if (times === 0) {
        /* THE ROOM OBSERVATION IS SPENT ONCE, by the first card to lead on
         * this axis. Ten cards share one room, so restating it is what made
         * the old version read as one sentence ten times. */
        if (primary.lead && !saidLead[primary.axis]) {
          saidLead[primary.axis] = true;
          sentences.push(primary.lead);
        }
        sentences.push(primary.text);
      } else if (primary.rider) {
        /* THE SAME FACT, SAID SHORT. A second card on this axis opens on the
         * rider rather than repeating the first card's sentence word for
         * word - which is why the riders are written to stand on their own. */
        sentences.push(primary.rider);
        usedRider[primary.rider] = true;
      } else {
        sentences.push(primary.text);
      }

      /* THE SECOND FACT, FROM A DIFFERENT AXIS. Unused riders are preferred; a
       * used one is taken rather than leaving the paragraph at one sentence,
       * because saying a true thing twice across ten cards beats saying
       * nothing on the tenth.
       *
       * A CARD THAT OPENED ON A RIDER TAKES TWO, because a rider is one short
       * sentence and one alone is a caption rather than a paragraph. */
      var want = (times === 0) ? 1 : 2, taken = 0, fallback = null;
      for (i = 0; i < clauses.length && taken < want; i++) {
        var c = clauses[i];
        if (c.axis === primary.axis || !c.rider || usedRider[c.rider]) {
          if (c.axis !== primary.axis && c.rider && !fallback) fallback = c.rider;
          continue;
        }
        usedRider[c.rider] = true;
        sentences.push(c.rider);
        taken++;
      }
      if (!taken && fallback) sentences.push(fallback);

      /* A PARAGRAPH THAT HAS ALREADY BEEN SAID IS NOT SAID AGAIN. Everything
       * above spreads the axes and varies the phrasing, and a rug can still
       * arrive with exactly ONE true thing about it - a modern rug in a modern
       * room, and nothing else the engine can gate on. The third such card has
       * no third wording left, and two cards reading "It is modern, like the
       * room." word for word is the copy-paste the user named.
       *
       * SO IT SHOWS THE SIZE SENTENCE ALONE, which is the instruction rather
       * than a shortfall: "where the data doesn't support a real observation,
       * show the size clause alone rather than padding it." An empty
       * `sentences` is what `paintWhy` reads as that case. */
      var joined = sentences.join(' ');
      if (usedSaid[joined]) return { sentences: [], warn: false };
      usedSaid[joined] = true;
      return { sentences: sentences, warn: false };
    });
  }

  function readBack() {
    var d = el('div', 'fit-read');
    var bits = [];
    /* ── THE ROOM IS NOT NAMED, ANYWHERE (2026-09-06) ───────────────────────
     * By direct decision of the user, after patio photographs came back read
     * as "living room". The label is removed from every customer-facing
     * surface; the room type still drives the SIZE, and that is stated below
     * rather than hidden.
     *
     * WHY REMOVING THE LABEL IS NOT HIDING THE ERROR, MEASURED. It reaches
     * the band, and through it the size. Measured across the six rooms on the
     * live index, the recommended size from one product pool:
     *
     * ── CORRECTION, 2026-09-07: THIS USED TO SAY "the room type reaches
     * `scoreOne` NOWHERE", AND THAT IS NO LONGER TRUE. `forEngine` carries
     * `room` so the guide's construction rules can exclude a rug too thick for
     * a kitchen door or an office caster. What was a STRUCTURAL guarantee is
     * now a MEASURED one, and the measurement is the reason it was accepted:
     * swept over 3 constrained rooms x 11 room styles x 5 floors, 165 sets,
     * the exclusion removed 33 of 1,351 rows and changed the top ten in
     * ZERO of them - the rugs it refuses never ranked anyway.
     *
     * SO A MISREAD ROOM COSTS NOTHING TODAY AND IS NOT GUARANTEED TO COST
     * NOTHING TOMORROW. It errs small in one direction (a living room misread
     * as a kitchen hides 33 rows nobody was going to be shown) and is a no-op
     * in the other (a kitchen misread as living simply fails to exclude, which
     * is where we already were). If the catalogue ever gains a high-pile rug
     * good enough to rank, this number stops being zero - re-measure it before
     * assuming it still holds.
     *
     *     living 290x300   bedroom 260x290   dining 250x340
     *     office 140x260   hallway/kitchen 100x230
     *
     * The three SEATING rooms sit within 0.87x-1.15x of each other - one or
     * two steps on the size ladder - so a misread AMONG them costs almost
     * nothing. A seating room read as a RUNNER room costs 3.78x, and that is
     * the misread worth catching. A patio with a sofa and a table is a
     * seating group, and the living band is the same rule; a narrow balcony
     * is not, and would be badly wrong. */
    if (read.floor_material) bits.push(read.floor_material + ' floor');
    if (read.colour_family) {
      bits.push(read.colour_family === 'neutral' ? 'mostly neutral'
                                                 : read.colour_family + ' tones');
    }
    d.appendChild(el('p', 'fit-read__line', 'From your photo: ' + bits.join(' · ')));
    /* WHAT IT COULD NOT TELL IS SAID OUT LOUD. An unclear answer reported as a
     * confident one is the failure this whole system is built to avoid. */
    /* `room` IS FILTERED OUT OF THIS LIST, AND NOT MERELY BECAUSE IT NAMES A
     * ROOM. The sentence's claim is that the field is NOT PART OF THE MATCH,
     * and for the room that is false: an unclear room falls back to the
     * living-room band and sizes the whole page. Saying it was excluded when
     * it was substituted is a worse fault than naming it. The uncertainty is
     * reported where it actually lands, on the size block. */
    /* THE TWO CAVEATS MOVED TO SCREEN 4 (2026-09-07), where the reading itself
     * is presented, and they are NOT repeated here. Saying "we could not tell
     * what your floor is made of" on the screen that shows the reading and
     * again one screen later is the ten-identical-sentences fault at the scale
     * of the flow. What stays here is the one-line context, so screen 5 can be
     * read without stepping back. */
    return d;
  }

  /* `labelRoom` IS DELETED, NOT LEFT UNUSED. A function that turns a room code
   * into a customer-facing phrase is exactly what would get wired back up, and
   * the decision is that no surface names the room. The same rule this repo
   * applied to `rug_details`, to `crea_craft` and to the inspired-by mode. */

  /* THE SIZE THIS ROOM WANTS, STATED ONCE, WITH NO SETTINGS ATTACHED.
   *
   * IT USED TO CARRY A ROW OF PLACEMENT CHIPS AND A SOFA SLIDER. Both are gone
   * by direct decision of the user: the customer sees the answer, not the
   * controls that produced it. What that costs is written down rather than
   * glossed - the bedroom `side_runners` layout is now unreachable, and with it
   * the "buy TWO" quantity path, and the living-room length now comes from the
   * analyser's own seat count instead of a slider the customer could correct.
   * The read-back line says when that count was a guess, which is the only
   * honest way to remove a control that was also a correction. */
  function sizeLine() {
    var d = el('div', 'fit-size');
    var sizes = results.out.items.length
      ? sizesFor(results.out.items[0].product, band) : [];
    var lo = sizes.length ? sizes[sizes.length - 1] : null;
    var hi = sizes.length ? sizes[0] : null;
    var range = (lo && hi && lo.option !== hi.option)
      ? imperial(lo.option) + ' to ' + imperial(hi.option)
      : (hi ? imperial(hi.option) : '');
    d.appendChild(el('p', 'fit-size__lead',
      'Sizes that suit this room: ' + range));
    if (band.rule) d.appendChild(el('p', 'fit-size__rule', band.rule + '.'));
    /* WHERE THE ROOM READ WAS UNSURE, IT IS SAID HERE - and here is the only
     * honest place for it, because the size is the ONLY thing the room type
     * touches. It reaches no part of the score: `roomRead` carries style,
     * floor, colour and pattern and no room at all. Reporting it in the
     * read-back's "not part of the match" list would be false, which is why it
     * is filtered out of that list and lands on the block it actually governs. */
    if (!read.room || read.room === 'unclear' ||
        (read.unclear || []).indexOf('room') >= 0) {
      d.appendChild(el('p', 'fit-note',
        'The photo did not make the layout obvious, so this is our general ' +
        'sizing for a room with seating. Measure the space if you can.'));
    }
    if (band.warning) d.appendChild(el('p', 'fit-note', band.warning));
    (band.notes || []).forEach(function (n) {
      d.appendChild(el('p', 'fit-note', n));
    });
    return d;
  }

  /* THE SIZE HALF OF A CARD'S REASON. Assembled here rather than in the engine
   * because only the client knows which size the customer is looking at - the
   * selector is a control and it changes under them - but the WORDS come from
   * the index, so there is still one copy of every string a customer reads. */
  function sizeClause(sz) {
    if (!sz) return '';
    /* THE SIZE AND NOTHING ELSE. It briefly carried the band's rule too, and
     * that rule is a property of the ROOM rather than of the rug - so all ten
     * cards ended with the same sentence about the front third of the sofa.
     * `sizeLine()` states it once, above the grid. */
    return window.FitEngine.fill(idx.reason_templates.size,
                                 { size: imperial(sz.option) });
  }

  function card(item, whyPick, after) {
    var p = item.product;
    var sizes = sizesFor(p, band);
    var best = sizes[0];
    /* NO `is-vetoed` MODIFIER ANY MORE. A vetoed rug is excluded from the set
     * rather than dimmed inside it, so the class could not fire - and a style
     * hook for a state the page cannot reach is a thing a future reader has to
     * work out is dead. */
    var c = el('article', 'fit-card');

    /* ── THE LIMITED-TIME BAR ──────────────────────────────────────────────
     * White on red, above the rug image, small. By direct decision of the user
     * and RECORDED IN THE AMENDMENTS: voice.md bans invented urgency and there
     * is no end date on this offer, so this is a deliberate exception taken
     * with the rule stated, the way the exclamation marks and the gold were.
     *
     * IT IS CLONED FROM AN AUTHORED SOURCE ELEMENT, like the risk line and for
     * the same reason: a literal here is a copy slot the marker test cannot
     * see, and the placeholder flag travels with the clone. */
    var urg = cloneSrc('[data-rf-urgency]', 'fit-card__urgent');
    /* THE CLOCK IS A SPAN INSIDE THE AUTHORED LINE, so slot 465 stays one copy
     * slot the marker test can see and the number is appended to whatever it
     * says rather than being interpolated into it. */
    urg.appendChild(document.createTextNode(' '));
    urg.appendChild(el('span', 'fit-card__clock', ''))
       .setAttribute('data-rf-countdown', '');
    c.appendChild(urg);

    var a = el('a', 'fit-card__link');
    var thumbWrap = null;
    a.href = '/products/' + p.h;
    /* THE LINK PRESELECTS THE SIZE ONCE THE VARIANT IS KNOWN, AND NOT A MOMENT
     * BEFORE. Shopify can only preselect by numeric variant id, so this cannot
     * be built at render time without a request per card. It is upgraded when
     * the customer's own action has already resolved one — and until then it is
     * exactly the link that was there before, so nothing is slower than it was. */
    function linkTo(sz, variant) {
      if (!sz) return;
      var v = variant;
      if (!v && VARIANTS[p.h] && VARIANTS[p.h].seen) {
        v = VARIANTS[p.h].seen[sz.option];
      }
      a.href = '/products/' + p.h + (v ? '?variant=' + v.id : '');
    }
    /* THE RUG ITSELF. Six text-only cards is not a rug recommendation - the
     * customer is choosing on appearance and the card showed none of it. */
    if (imageFor(p)) {
      var th = document.createElement('img');
      th.className = 'fit-card__thumb';
      /* SAME CORS MODE AS THE PREVIEW, OR THE PREVIEW CANNOT READ ITS PIXELS.
       * A browser caches an image fetched WITHOUT `crossorigin` and then reuses
       * that entry for a later CORS request of the same URL, which then fails
       * the check - measured: the card thumbnail poisoned the preview, and the
       * server headers were correct the whole time (`vary: Origin`,
       * `access-control-allow-origin` both present). Setting it here makes the
       * first fetch CORS-mode so both share one clean entry. */
      th.crossOrigin = 'anonymous';
      th.src = imageFor(p);
      th.alt = '';
      th.loading = 'lazy';
      th.decoding = 'async';
      /* A POSITIONED HOST FOR THE BADGE, AND IT WAS ALREADY BEING LOOKED FOR.
       * `rug-wishlist.js`'s `imageHost()` tries `.fit-card__thumb-wrap` before
       * falling back to the card itself, and `rug-wishlist.css` already gives
       * that class `position: relative` — the finder simply never built one, so
       * the heart fell back to the card. Adding it puts the badge over the
       * IMAGE rather than the card, which is where every other surface puts it,
       * and moves the heart onto the image for the same reason. */
      thumbWrap = el('div', 'fit-card__thumb-wrap');
      thumbWrap.appendChild(th);
      /* THE RANK, ON THE PICTURE, AND NOT IN THE TOP-LEFT CORNER ANY MORE.
       *
       * IT WAS SITTING UNDER THE SALE BADGE. Both were absolutely positioned
       * at the top-left - the badge at 16px, this at 8px - so on every
       * discounted rug the number was behind it. The badge did NOT move: it is
       * a shop-wide convention rendered identically on the home page, the
       * favourites grid and every collection, and moving it here would make
       * the finder the one surface where SALE is somewhere else. The heart
       * holds the top-right for the same reason. The bottom-left was empty, it
       * belongs to this page alone, and it is one line. */
      var rk = el('span', 'fit-card__rank', String(item.rank));
      rk.setAttribute('aria-hidden', 'true');
      thumbWrap.appendChild(rk);
      a.appendChild(thumbWrap);
    }
    var nameEl = el('h3', 'fit-card__name', shortName(p, VARIANTS[p.h]));
    a.appendChild(nameEl);
    c.appendChild(a);

    /* THE STARS SIT UNDER THE MODEL NAME, and the slot is created now while
     * the id is not yet known. `priceFor` fills it from the SAME lookup it
     * makes for the price, so the stars cost nothing extra and arrive in the
     * same repaint. An empty box is never left behind: the wrapper carries no
     * height of its own and nothing is appended unless Loox has an id to
     * answer for. */
    var starSlot = el('div', 'fit-card__starslot');
    c.appendChild(starSlot);

    /* ── THE COLOUR IS THE POSITION, AND THE LABEL SAYS SO ──────────────────
     * The band is a FIXED DISTRIBUTION over the set - rank 1 green, the bottom
     * fifth red, the rest yellow - so the label may not make a quality claim
     * the colour would then contradict. "Good fit" under a red card is the
     * page arguing with itself, and that is what these three used to be.
     *
     * RED MEANS NINTH OR TENTH. It does not mean poor, and the two things a
     * customer has to read it against are both on this card: the label, which
     * says position in words, and the SCORE two lines below, which is
     * independent of rank and reads 8.0 on the median tenth card. */
    var tierText = tierSentence(item.tier, item.setSize);
    var tier = el('p', 'fit-card__tier fit-card__tier--' + item.tier, tierText);
    tier.insertBefore(el('span', 'visually-hidden',
                         'Ranked ' + item.rank + ' of ' +
                         results.out.items.length + '. '), tier.firstChild);
    c.appendChild(tier);
    /* THE GREEN FRAME GOES ON THE CARD, not on the image, so it reads as one
     * object being singled out rather than a border drawn on a photograph. */
    c.className = 'fit-card fit-card--' + item.tier;

    /* ── THE FREE GIFTS, THE PRODUCT PAGE'S OWN ROW ────────────────────────
     * The same five tiles, the same struck prices, the same mystery tile,
     * WITHOUT the "free gift value" total - by direct decision of the user.
     * Rendered once by Liquid into a hidden source and cloned per card, so the
     * finder and the product page cannot show different gifts: there is one
     * snippet and the five gifts live in theme settings.
     *
     * IT IS FRAMED SEPARATELY so it does not blur into the rest of the card.
     * The five images are the SAME five files on all ten cards, so the browser
     * fetches five and reuses them; the repetition costs layout, not bytes. */
    /* ── THE PRICE SITS DIRECTLY ABOVE THE GIFTS ──────────────────────────
     * By direct decision of the user (2026-09-07): "Out of the add-to-cart
     * button. Put it directly above the gifts row. The 50% off, the new price,
     * the old price - all three, in that position."
     *
     * It was inside the button for one day. The reason for moving it is the
     * gifts: five things given free read as a discount on the number ABOVE
     * them and as decoration under a button. The order on the card is now the
     * order of the argument - here is the price, and here is what comes with
     * it.
     *
     * IT IS CREATED HERE, NOT WHERE IT USED TO BE, because the card is built
     * top to bottom and the gifts are the next thing appended. Everything
     * about what it CONTAINS is unchanged: it starts as the index's FROM-price
     * and `paintSale` replaces it with the exact variant price, the discount
     * and the struck original the moment the lookup lands. The index carries
     * no compare-at price, so there is nothing to strike through until then
     * and "from" is the only true thing to say. */
    var priceLine = null;
    if (p.px != null) {
      /* THE FROM-PRICE ON THE CARD ITSELF, so `priceOrder` can sort the page
       * before a single lookup has landed. It is read back rather than closed
       * over because the sort works on DOM nodes, which is what it reorders. */
      c.setAttribute('data-from-price', String(p.px));
      priceLine = el('span', 'fit-card__price', 'from ' + money(p.px));
      var pw = el('div', 'fit-card__pricerow');
      pw.appendChild(priceLine);
      c.appendChild(pw);
    }

    var gifts = $('[data-rf-gifts]', root);
    if (gifts && gifts.firstElementChild) {
      var gw = el('div', 'fit-card__gifts');
      gw.appendChild(gifts.firstElementChild.cloneNode(true));
      c.appendChild(gw);
    }

    /* ── THE SCORE, AS A NUMBER, WITH EVERY CAVEAT RECORDED ────────────────
     * By direct decision of the user (2026-09-06), against this module's own
     * standing objection, which is unchanged and is why it is written down
     * here as well as in the amendments:
     *
     *   - THE SIX WEIGHTS ARE DECLARED PRIORS, NOT FITTED. Nothing in this
     *     repo has ever measured them against a purchase.
     *   - 45 OF THE STYLE MATRIX'S 88 CELLS ARE TIER C - "NO SOURCE FOUND" -
     *     and style carries the largest weight at 0.45.
     *   - THE SCALE ONLY USES PART OF ITSELF. Measured over 7,056 simulated
     *     room reads on the live index: p05 6.2, median 8.5, p95 9.5. A
     *     customer reading "8.5/10" will read it as a school mark, and it is
     *     not one - it is the middle of this catalogue's ordinary range.
     *
     * WHAT MAKES IT SAFE ENOUGH TO PRINT IS THAT IT IS INDEPENDENT OF THE
     * COLOUR. The colour says where the rug sits; the number says how well it
     * scored, and a red card reading 8.0 corrects its own colour. That is the
     * whole reason this is the raw index rather than a percentile: a percentile
     * would say the same thing as the rank twice, and the red card would then
     * carry a low number too. */
    /* ── THE PAIR: THIS ROOM NOW, AND THIS ROOM WITH THIS RUG ──────────────
     * By direct decision of the user (2026-09-07), after both candidates were
     * measured across 48 rooms:
     *
     *     A  after = fit x 10          median spread 0.86 pts, 29% under half
     *     B  after = before + 4 x fit  median spread 0.34 pts, 73% under half
     *
     * B collapses three rooms in four into a set of near-identical numbers, so
     * A is what ships.
     *
     * IT REPLACES THE STANDALONE SCORE RATHER THAN SITTING BESIDE IT. A is the
     * compatibility score rescaled - arithmetically the same number the card
     * printed before, to the decimal - so printing both would print 9.3 twice
     * on one card, which is worse than either alone. There is ONE rug-room
     * measurement in this system and this is it.
     *
     * ── WHAT THE PAIR IS AND IS NOT, STATED ONCE ─────────────────────────
     * The two numbers are on DIFFERENT SCALES: the before is capped at 7 by
     * design and the after routinely reads past 9, so the LIFT is not itself a
     * measurement of anything. What varies meaningfully is the BEFORE across
     * rooms - a 5.4 room gains more from the same rug than a 6.8 room - and
     * that difference is real. It is printed as specified and not dressed up.
     *
     * THE COLOURS ARE FIXED RED AND GREEN, not the tier ramp. The tier is a
     * statement about POSITION and it still owns the label and the card frame;
     * the pair is a statement about this room before and after, and colouring
     * the after by rank would make the tenth card's "after" read as a warning
     * about a rug that scored 8.0. */
    if (item.score !== null && item.score !== undefined) {
      var pair = el('div', 'fit-pair');
      var haveBefore = !!(roomRead && roomRead.scored);
      if (haveBefore) {
        var bfr = el('div', 'fit-pair__side fit-pair__side--before');
        bfr.appendChild(el('span', 'fit-pair__lab', 'Your room now'));
        bfr.appendChild(el('span', 'fit-pair__num', roomRead.score.toFixed(1)));
        pair.appendChild(bfr);
        var arw = el('span', 'fit-pair__arrow', '→');
        arw.setAttribute('aria-hidden', 'true');
        pair.appendChild(arw);
      }
      /* NO BEFORE MEANS NO RED HALF. The class is set here because this is the
       * only place that knows whether the pair has two sides - a `:has()` was
       * not used, having already failed to match silently once on this page. */
      if (!haveBefore) pair.className += ' fit-pair--solo';
      var aft = el('div', 'fit-pair__side fit-pair__side--after');
      /* WITHOUT A BEFORE - an outdoor space - THE AFTER STANDS ALONE and is
       * labelled as what it is rather than as half of a comparison that is not
       * being made. This is exactly the card the page shipped before the pair
       * existed. */
      aft.appendChild(el('span', 'fit-pair__lab',
                         haveBefore ? 'With this rug' : 'Fit for your space'));
      /* THE AFTER-NUMBER IS THE RANK LADDER when there is a before to anchor it
       * on. See `afterLadder` for what that number is and, more importantly,
       * what it is not.
       *
       * AN OUTDOOR SPACE KEEPS ITS MEASURED NUMBER. There is no before, so
       * there is nothing to anchor a lift on, and inventing one would be the
       * only dishonest thing in this arrangement - the standalone figure is
       * still `item.score` rescaled, which is a real measurement of this rug
       * against this space, and it is labelled as a fit rather than as an
       * after. */
      var aftNum = el('span', 'fit-pair__num',
                      haveBefore && typeof after === 'number'
                        ? after.toFixed(1)
                        : (Math.round(item.score * 100) / 10).toFixed(1));
      /* MARKED ONLY WHEN IT IS A LADDER VALUE. `priceOrder` re-attaches the
       * ladder to whatever card ends up at each position, and it must not
       * touch the OUTDOOR number - that one is `item.score` rescaled, a real
       * measurement of this rug against this space, and it belongs to its own
       * rug wherever the price sort puts it. The attribute is the only thing
       * that tells the two apart after the fact. */
      if (haveBefore && typeof after === 'number') {
        aftNum.setAttribute('data-rf-after', '');
      }
      aft.appendChild(aftNum);
      pair.appendChild(aft);
      c.appendChild(pair);
    }

    /* ── THE SIZE PICKER IS GONE; THE ANSWER TAKES ITS PLACE (2026-09-06) ──
     * By direct decision of the user: "the results still use the old
     * add-to-cart and the old size recommendation - replace both with what the
     * product-page popup already does". That popup names ONE size, large, with
     * one button under it, and it carries no picker at all.
     *
     * WHAT IT COSTS, STATED RATHER THAN GLOSSED: a customer who wants a
     * different size can no longer change it here. The card's link already
     * carries `?variant=` for the recommended size once it resolves, so the
     * product page opens on that size with the full picker beside it - which
     * is one click, and it is where a customer who is choosing a size rather
     * than accepting one was always going to end up.
     *
     * `chosen` STAYS AS A VARIABLE even though nothing moves it now. It is
     * read by the price lookup, the visualiser hand-over, the cart line and
     * the reason's size clause, and collapsing four readers onto `best`
     * directly would mean four edits the day a picker comes back. */
    var chosen = best;

    /* THE PRICE MUST BE THE PRICE OF THE THING THE BUTTON ADDS.
     *
     * `p.px` is the product's FROM-price - the smallest size in the pool - and
     * it was fine beside a link to a product page. Beside a size selector and an
     * Add button it is not: the card can say $43.20 while the button charges
     * $299.51 for the size we ourselves recommended.
     *
     * IT IS NOT CARRIED IN THE INDEX, AND THAT WAS MEASURED RATHER THAN
     * ASSUMED. Every product in the catalogue does price linearly by area, so
     * one rate per product looked like a 27 KB-index answer - but replaying it
     * over the real workbook reproduces 26,687 rows to the cent and misses
     * 1,303 by exactly a penny. A price that is a penny wrong next to a buy
     * button is wrong, and it would be wrong silently.
     *
     * So it comes from Shopify, which is the only authority on what the cart
     * will charge, and it cannot drift because there is no copy of it. Until it
     * lands the line keeps saying "from", which is true. */
    /* `priceLine` IS BUILT AND APPENDED ABOVE THE GIFTS - see there. It was
     * created at this point while it lived inside the Add button (2026-09-07,
     * for one day); the card is assembled top to bottom, so it has to be made
     * before the gifts row rather than after the reasons. */

    /* THE SALE TREATMENT ARRIVES WITH THE EXACT PRICE, NOT BEFORE IT, AND THAT
     * IS A PROPERTY OF WHAT THIS CARD KNOWS.
     *
     * The fit index carries `p.px` — the product's FROM-price — and no
     * compare-at price at all, so at render time there is nothing to strike
     * through and no percentage to compute. Both arrive from the SAME variant
     * lookup that already runs for the recommended size (`priceFor(chosen)`
     * below), which is also the only authority on what the cart will charge.
     *
     * SO THE CARD SETTLES ONCE, AND IT ALREADY DID. The "from" line was always
     * replaced by the exact price a moment later; the badge and the was-price
     * land in that same repaint rather than adding a second one. Carrying a
     * compare-at in the index would remove the delay and was NOT done: it is a
     * rebuild-and-republish of an index this file reads at 27 KB gzipped, to
     * save one repaint that already happens.
     *
     * THE PERCENTAGE IS DERIVED FROM THE TWO FIGURES THIS ROW PRINTS — the
     * same rule `snippets/price.liquid` and the favourites grid follow, so a
     * rug cannot show one percentage here and another on its own product page.
     * Floored, because understating a discount is a claim we can meet. */
    function paintSale(v) {
      if (!priceLine) return;
      var now = v.price, was = v.compare_at_price;
      var onSale = typeof was === 'number' && was > now;

      priceLine.textContent = '';
      if (onSale) {
        var off = Math.floor((was - now) * 100 / was);
        if (off > 0) {
          var o = el('span', 'rz-off', off + '% off');
          /* aria-hidden for the reason every other surface gives: the two
           * prices beside it carry the same fact as text. */
          o.setAttribute('aria-hidden', 'true');
          priceLine.appendChild(o);
        }
      }
      priceLine.appendChild(el('span', 'fit-card__now', money(now / 100)));
      if (onSale) {
        var w = document.createElement('s');
        w.className = 'fit-card__was';
        w.textContent = money(was / 100);
        priceLine.appendChild(w);
      }
      priceLine.classList.add('is-exact');

      /* ONE BADGE PER CARD, whatever happens afterwards. A customer changing
       * size re-runs this, and without the guard a card would collect a badge
       * per change. */
      if (onSale && thumbWrap && !thumbWrap.querySelector('.rz-bcard__badge')) {
        var b = el('span', 'rz-bcard__badge', 'SALE');
        b.setAttribute('aria-hidden', 'true');
        thumbWrap.appendChild(b);
      }
    }
    function priceFor(sz) {
      if (!priceLine || !p.h || !sz) return;
      var want = sz;
      resolveVariant(p.h, sz.option).then(function (v) {
        /* A LATE ANSWER FOR A SIZE THAT IS NO LONGER SELECTED IS DISCARDED.
         * Two resolves can be in flight after a quick change of mind, and the
         * slower one must not repaint the faster one's price. */
        if (!v || chosen !== want) return;
        /* THE LINK GETS THE VARIANT TOO. The same lookup that prices the size
         * also identifies it, so anyone who clicks through to the product page
         * lands on the size we recommended rather than the product's default -
         * which is the same friction the button removes, for the customer who
         * would rather look before buying. */
        linkTo(want, v);
        /* THE REVIEW STARS, FROM THE ID THIS LOOKUP JUST CACHED. Once only -
         * `priceFor` runs again whenever the customer changes size, and a
         * second widget for the same product would be a second row of stars. */
        if (!starSlot.firstChild) {
          var st = looxStars(p.h);
          if (st) starSlot.appendChild(st);
        }
        /* THE NAME, FROM THE TITLE THIS LOOKUP JUST CACHED. Idempotent: the
         * same string is written on every later size change. */
        if (nameEl) nameEl.textContent = shortName(p, VARIANTS[p.h]);
        if (!priceLine) return;
        paintSale(v);
      }).catch(function () { /* the "from" line is still true, and no stars */ });
    }
    /* ── THE REASON: A SHORT PARAGRAPH, AND TEN OF THEM READ AS TEN ────────
     * By direct decision of the user (2026-09-06): "a short paragraph that
     * reads like someone looked at the room and thought about it... Different
     * for every rug in the set. Ten cards should read as ten observations, not
     * one sentence with the adjectives swapped."
     *
     * IT WAS ONE CLAUSE AND THAT IS THE WHOLE PROBLEM THIS SOLVES. Ten cards
     * share ONE room, so any template that opens on the room opens identically
     * ten times. The engine therefore returns AXES rather than sentences -
     * tone, style, colour family, accent, pattern, plus chroma as a rider - and
     * each carries three interchangeable parts: a LEAD about the room, a TEXT
     * about this rug, and a RIDER for a second sentence.
     *
     * `composeWhy()` in `render()` does the choosing, because only a pass over
     * the whole set can see what the cards above already said: the room
     * observation is spent ONCE, by the first card that leads on that axis,
     * and later cards on the same axis open on the rug instead. The rider then
     * comes from a DIFFERENT axis, preferring one no earlier card has used.
     *
     * WHERE THE DATA SUPPORTS NO OBSERVATION THE CARD SHOWS THE SIZE ALONE.
     * Padding it with something generic is what the chips did. */
    var whyLine = el('p', 'fit-card__why');
    function paintWhy(sz) {
      var parts = (whyPick && whyPick.sentences) ? whyPick.sentences.slice() : [];
      /* THE SIZE SENTENCE IS THE FALLBACK, NOT AN ADDITION. "Where the data
       * doesn't support a real observation, show the size clause alone rather
       * than padding it" - so it appears only when there is nothing else to
       * say. Appending it to a paragraph would print the size twice, three
       * lines apart, in 0.9rem text above a block that prints it at 1.8rem. */
      if (!parts.length) {
        var sc = sizeClause(sz);
        if (sc) parts.push(sc);
      }
      whyLine.textContent = parts.join(' ');
      whyLine.className = 'fit-card__why' +
        (whyPick && whyPick.warn ? ' fit-card__why--warn' : '');
      whyLine.hidden = !parts.length;
    }
    paintWhy(chosen);
    c.appendChild(whyLine);

    /* ── THE SIZE ANSWER AND THE BUTTON, AS THE PRODUCT-PAGE POPUP DOES IT ──
     * (2026-09-06, by direct decision: "One definition, two callers. Don't
     * build a second version of a thing the popup already does well.")
     *
     * THE SHAPE IS `rz-vsize`'s, DELIBERATELY: a quiet lead, the imperial size
     * as the largest thing in the block, the metric under it, and one
     * full-width button. Same order, same emphasis, same wording for the
     * button, so a customer who saw it on a product page meets the same
     * control here.
     *
     * WHAT IS NOT COPIED, AND WHY. The popup prints the band's RULE under the
     * button; ten cards printing it would repeat one 96-character sentence
     * about the front third of the sofa ten times - the rule is a property of
     * the ROOM, not of the rug, and `sizeLine()` states it once above the
     * grid. That was measured and fixed once already and is not being undone.
     * And the popup's lead names the room ("For a living room like this,
     * buy"); no surface names the room any more, so it does not.
     *
     * IT SITS ABOVE "See it in your room", WHICH REVERSES AN EARLIER
     * DECISION AND FOLLOWS THE POPUP INSTEAD. The old order put the buy
     * control last so a customer who rendered their room did not have to
     * scroll back up past it. The popup answered the same question the other
     * way - "burying it under an image they have to scroll past is how 'buy
     * 200x300' becomes something they half-remember" - and the popup is the
     * thing this is told to match. The render still lands at the bottom of the
     * card, so the arrangement is now identical on both surfaces. */
    var buyNote = null;
    /* The buy block, hoisted so the risk line can be appended INSIDE it - the
     * price, the button and the promise are one object on the card. */
    var buyBlock = null;
    if (best) {
      var sizeBox = buyBlock = el('div', 'fit-buy');
      sizeBox.appendChild(el('p', 'fit-buy__lead', 'Recommended size'));
      sizeBox.appendChild(el('p', 'fit-buy__big', imperial(best.option)));
      var metric = metricLabel(best.option);
      if (metric) sizeBox.appendChild(el('p', 'fit-buy__sub', metric));
      c.appendChild(sizeBox);

      if (p.h) {
        /* THE QUANTITY IS ALWAYS ONE NOW, AND THE REASON IS RECORDED BECAUSE
         * THE OLD BRANCH WAS CORRECT. `side_runners` is the one layout that
         * needs two rugs - its own note reads "buy TWO" - and this button used
         * to read "Add both (2)". That layout was only reachable by pressing a
         * placement chip, and the chips were removed on 2026-09-06;
         * `choosePlacement` walks the room's list most-recommended first and
         * `side_runners` is third of four in a bedroom, so it is now reached
         * only if two better layouts refuse. If it ever is, `band.notes` still
         * renders the "buy TWO" line above the grid. A quantity rule that can
         * only fire in a state the page cannot reach is a thing a future
         * reader has to work out is dead. */
        var qty = 1;
        var buy = el('button', 'fit-buy__add');
        buy.type = 'button';
        /* THE LABEL IS A COPY SLOT, CLONED FROM THE AUTHORED SOURCE so the
         * marker test can see it. It is the button's only child now - the
         * price moved above the gifts row on 2026-09-07 - but it stays a SPAN
         * rather than becoming the button's text, because the click handler
         * rewrites the wording ("Adding...", "Added") and writing to
         * `buy.textContent` would delete whatever else the control holds. */
        var buyLab = cloneSrc('[data-rf-addcta]', 'fit-buy__addlab', 'span');
        buy.appendChild(buyLab);
        buyNote = el('p', 'fit-card__buynote');
        buyNote.hidden = true;

        buy.addEventListener('click', function (ev) {
          ev.stopPropagation();
          if (buy.disabled) return;
          var want = chosen || best;
          buy.disabled = true;
          /* THE WORDING CHANGES, THE PRICE STAYS. Writing to `buy.textContent`
           * would delete the price element the customer is deciding on - the
           * label is its own span for exactly this reason. */
          var label = buyLab.textContent;
          buyLab.textContent = 'Adding…';
          buyNote.hidden = true;
          resolveVariant(p.h, want.option).then(function (v) {
            if (!v) throw new Error('no_variant');
            if (v.available === false) throw new Error('unavailable');
            linkTo(want, v);
            /* THE DRAWER OPENS BECAUSE `RugCart.add` DISPATCHES THE SHAPE THE
             * THEME LISTENS FOR - a deferred promise on a real
             * `CartLinesUpdateEvent` with `action: 'add'` - and the cart
             * drawer opens on that. This file used to POST the line itself and
             * dispatch a plain CustomEvent, which every one of the theme's
             * twelve listeners ignored because none of them found `.promise`.
             * There is no second implementation here; `addToCart` is a thin
             * call into `rug-cart.js`, which is the one copy for both
             * surfaces. */
            return addToCart(v.id, qty).then(function () { return v; });
          }).then(function (v) {
            /* RECORDED HERE, WHERE THE CART HAS ACCEPTED THE LINE - not on the
             * press. A failed add must not pin a card to the page, and a card
             * pinned by a press that did nothing would hold a slot for a rug
             * the customer is not buying. `keptAndRotated` reads this. */
            inCart[p.h] = true;
            buyLab.textContent = 'Added — it is in your cart';
            buyNote.hidden = false;
            buyNote.textContent = '';
            buyNote.appendChild(document.createTextNode(
              imperial(want.option) + ' added. '));
            var go = el('a', 'fit-card__cartlink', 'View cart');
            go.href = '/cart';
            buyNote.appendChild(go);
            recordChoice(p, want, item);
            setTimeout(function () {
              buyLab.textContent = label; buy.disabled = false;
            }, 2500);
          }).catch(function (e) {
            buyLab.textContent = label;
            buy.disabled = false;
            buyNote.hidden = false;
            var why = String(e && e.message || '');
            /* THREE DIFFERENT FACTS, THREE DIFFERENT SENTENCES. Only the first
             * two are statements about the product; the third is about us, and
             * saying "not listed" for a failed lookup would be a code fault
             * wearing a catalogue verdict. */
            buyNote.textContent = why === 'no_variant'
              ? 'That size is not listed for this rug. Open the product page to see what is.'
              : why === 'unavailable'
                ? 'That size is out of stock. Open the product page for the others.'
                : why === 'lookup_failed'
                  ? 'We could not check that size just now. Please try again.'
                  : 'We could not add it just now. Please try again.';
          });
        });
        sizeBox.appendChild(buy);
        c.appendChild(buyNote);
      }
    }

    /* ── THE RISK LINE, DIRECTLY UNDER THE BUTTON, INSIDE THE SAME FRAME ────
     * Thirty days, a full refund, no questions - stated as a FACT, with a green
     * money-back tick at the start of it. By direct decision of the user it
     * sits inside the buy block rather than under the card, so the promise and
     * the price are one object.
     *
     * IT IS NOT THE GUARANTEE. §4's wording is used whole or not at all, and it
     * IS used whole on this screen: `rf-guarantee` prints both lines beneath
     * the ten cards. That is what licenses a short factual line here, exactly
     * as `rug-home-guarantee` at the foot of the product page licenses the risk
     * panel under Add to cart. IF THOSE TWO LINES EVER LEAVE SCREEN 4, THIS
     * LINE GOES WITH THEM.
     *
     * THE TICK IS DRAWN, NOT AN EMOJI, and it takes `--rzg-green` - the one
     * declared green, granted in rug-shared.css. A second green here is how the
     * finder's tick and the product page's would come to differ. */
    var riskSrc = $('[data-rf-risk]', root);
    if (riskSrc && riskSrc.textContent.trim() && buyBlock) {
      var risk = el('p', 'fit-card__risk');
      var tick = document.createElement('span');
      tick.className = 'fit-card__tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.innerHTML = '<svg viewBox="0 0 24 24" focusable="false" role="presentation">' +
        '<circle cx="12" cy="12" r="11"/><path d="M6.8 12.4 L10.4 16 L17.2 8.6"/></svg>';
      risk.appendChild(tick);
      risk.appendChild(el('span', 'fit-card__risktext', riskSrc.textContent.trim()));
      if (riskSrc.hasAttribute('data-rz-placeholder')) {
        risk.setAttribute('data-rz-placeholder', '');
      }
      buyBlock.appendChild(risk);
    }

    /* ── THE TWO BUTTONS, SIDE BY SIDE, THE SAME SIZE ───────────────────────
     * "See it in your room" and "Get this rug", by direct decision. The second
     * is the card's own product link wearing a button: it opens the product
     * page on the size we recommended, which is where a customer who wants to
     * choose a different size was always going to end up. It is an ANCHOR, not
     * a button, because it navigates - a button that navigates is a thing no
     * middle-click, no long-press and no screen reader can predict.
     *
     * THE ROW RENDERS EVEN WITH ONE BUTTON IN IT. "See it in your room" needs a
     * configured endpoint, a photo and a rug image; with any of those missing
     * the row carries "Get this rug" alone rather than disappearing, because
     * that button is the one that does not depend on anything.
     */
    var acts = el('div', 'fit-card__acts');

    if (VISUALISER && photo && imageFor(p)) {
      var see = el('button', 'fit-card__see', 'See it in your room');
      see.type = 'button';
      /* stopPropagation, NOT a reference check in the card's own handler. The
       * previous version of this button used `if (e.target === see) return;`
       * inside that handler, which made `see` a name the handler depended on -
       * and removing the button then left a dangling reference. This keeps the
       * two independent. */
      see.addEventListener('click', function (ev) {
        ev.stopPropagation();
        /* THE SIZE THEY CHOSE, NOT THE ONE WE OPENED ON. The select can have
         * moved since the card was drawn, and previewing a different size than
         * the one about to be bought is the same defect as adding one. */
        openVisualiser(c, p, chosen || best);
      });
      acts.appendChild(see);
    }

    /* ── THE SECOND CONTROL OPENS A POPUP; IT NO LONGER LEAVES THE PAGE ─────
     * It was an `<a>` to the product page. By direct decision of the user
     * (2026-09-07): "They go and they don't come back - the flow is lost."
     *
     * IT IS A BUTTON, NOT A LINK WITH ITS NAVIGATION SUPPRESSED. A link that
     * does not navigate still offers "open in a new tab" on a long press and
     * still reads as a link to a screen reader - both of which promise exactly
     * the trip this change exists to prevent.
     *
     * THE CARD'S OWN IMAGE AND NAME ARE STILL A LINK to the product page, and
     * deliberately: a customer who genuinely wants the full page should have
     * it. What is gone is the button that made leaving the obvious next step.
     */
    if (p.h) {
      var get = document.createElement('button');
      get.type = 'button';
      get.className = 'fit-card__get';
      var getLab = cloneSrc('[data-rf-getcta]', 'fit-card__getlab', 'span');
      get.appendChild(getLab);
      get.addEventListener('click', function (ev) {
        ev.stopPropagation();
        openDetails(p);
      });
      acts.appendChild(get);
    }
    if (acts.childNodes.length) {
      /* THE CLASS IS SET HERE BECAUSE ONLY THIS LINE KNOWS THE COUNT. A CSS
       * `:has(> * + *)` was tried first and did not match on the live page,
       * silently, leaving two buttons the requirement calls "the same size"
       * 22px apart. See `.fit-card__acts--two`. */
      if (acts.childNodes.length > 1) acts.className += ' fit-card__acts--two';
      c.appendChild(acts);
    }

    if (VISUALISER && photo && imageFor(p)) {
      /* THE NOTICE SITS WITH THE CONTROL, before it is pressed - the same rule
       * the product-page block follows. */
      c.appendChild(el('span', 'fit-card__seenote',
        'AI preview — approximate. Your photo is not saved.'));
    }

    c.addEventListener('click', function () {
      recordChoice(p, chosen, item);
    });
    /* Asked once, for the size that is selected. resolveVariant() caches per
     * product, so the six cards over three distinct rugs cost three lookups,
     * and the Add button inherits the answer rather than paying for it again. */
    priceFor(chosen);
    return c;
  }

  /* Builds the visualiser's own markup inside the card and hands it the photo.
   * The STRUCTURE has to match what rug-visualiser.js queries; it is created
   * here rather than rendered by Liquid because a card is created by this file
   * at runtime and there is no server render to hang it off. */
  function visBox(cardEl) {
    var box = $('[data-rug-vis]', cardEl);
    if (box) return box;
    box = el('div', 'rug-vis rug-vis--inline');
    box.setAttribute('data-rug-vis', '');
    var mk = function (tag, cls, attr) {
      var n = el(tag, cls);
      if (attr) n.setAttribute(attr, '');
      return n;
    };
    box.appendChild(mk('div', 'rug-vis__challenge', 'data-vis-turnstile'));
    var note = el('p', 'rug-vis__note',
      'An AI impression, not a photograph — the pattern and the proportions are '
      + 'approximate, so trust the measurements rather than the picture. Your photo is sent '
      + 'to Google’s image model to make this one picture and is not saved by us '
      + 'or shown to anyone.');
    box.appendChild(note);
    var st = mk('div', 'rug-vis__status', 'data-vis-status'); st.hidden = true;
    box.appendChild(st);
    var fb = mk('div', 'rug-vis__fallback', 'data-vis-fallback'); fb.hidden = true;
    box.appendChild(fb);
    var res = mk('div', 'rug-vis__result', 'data-vis-result'); res.hidden = true;
    var im = el('img', 'rug-vis__img'); im.setAttribute('data-vis-img', '');
    im.alt = 'Your room with this rug on the floor';
    res.appendChild(im);
    var cap = el('p', 'rug-vis__caption'); cap.setAttribute('data-vis-caption', '');
    res.appendChild(cap);
    box.appendChild(res);
    cardEl.appendChild(box);
    return box;
  }

  function openVisualiser(cardEl, p, size) {
    if (!window.RugVisualiser) return;
    var box = visBox(cardEl);
    box.setAttribute('data-vis', JSON.stringify({
      endpoint: VISUALISER,
      sitekey: VIS_SITEKEY,
      rug: imageFor(p),
      shape: p.sh || '',
      shapeIsOwn: true,
      size: size ? imperial(size.option) : '',
      handle: p.h || '',
      sku: p.sku || '',
      title: p.n || '',
      surface: 'finder'
    }));
    /* The FULL canvas, not the analyser's downscale — see encodeCanvas(). */
    window.RugVisualiser.open(box, { canvas: photo.canvas, w: photo.w, h: photo.h });
  }

  function allVetoedNotice() {
    /* IT USED TO SAY "They are below, shown dimmed". They are not below any
     * more - a vetoed rug no longer fills a slot - so the sentence would be
     * describing a grid that is not there. */
    var d = el('div', 'fit-empty');
    d.appendChild(el('p', 'fit-empty__lead',
      'Nothing we make would truly work in this room.'));
    d.appendChild(el('p', null,
      'Every rug that fits this room at this size sits so close in tone to ' +
      'your floor that it would read as part of it rather than as a rug. ' +
      'A rug clearly darker or clearly lighter than your floor is what this ' +
      'room needs, and we do not currently stock one in this size.'));
    return d;
  }

  function nothingFits() {
    var d = el('div', 'fit-empty');
    var why = (band && !band.ok) ? band.reason
      : 'nothing we make fits that layout in this room';
    d.appendChild(el('p', 'fit-empty__lead', 'We have nothing that fits that.'));
    d.appendChild(el('p', null, why + '.'));
    /* IT USED TO SAY "Try instead: floating" AND NAME A CHIP. With the chips
     * gone that would be an instruction the customer cannot follow, and
     * `choosePlacement` has already tried every alternative before this line is
     * reached - so reaching it means none of them worked either. */
    return d;
  }

  function tapeCard() {
    var d = el('aside', 'fit-tape');
    var sizes = results.out.items.length
      ? sizesFor(results.out.items[0].product, band) : [];
    var s = sizes[0];
    d.appendChild(el('h3', null, 'Before you buy'));
    d.appendChild(el('p', null,
      'Mark ' + (s ? imperial(s.option) : 'the size') + ' on your floor ' +
      'with masking tape and stand where you would sit. It is what IKEA tells ' +
      'its own customers to do, and it is more reliable than any picture — ' +
      'including ours.'));
    return d;
  }

  function footer() {
    var o = results.out;
    var d = el('p', 'fit-footer');
    d.textContent = o.considered + ' rugs considered · ' + o.vetoed +
      ' set aside as too close in tone to your floor';
    return d;
  }

  /* ── the outcome log ─────────────────────────────────────────────────────
   * Written into Shopify CART ATTRIBUTES, so it lands on the order in the admin
   * permanently and costs nothing. Without it, every complaint is resolved by
   * hand-tuning a weight, which is how a scoring model becomes a pile of
   * special cases. With it, a recommendation is REPRODUCIBLE after the fact:
   * replay the tuple against the same index hash and the same six rugs come
   * back, so a complaint is debuggable rather than arguable. */
  function recordChoice(p, size, item) {
    var attrs = {
      attributes: {
        _fit_room: state.room,
        _fit_placement: state.placement,
        _fit_input: state.sizeKey,
        _fit_floor: read.floor_material || 'unknown',
        _fit_anchored: String(!!(read.white_reference)),
        _fit_recommended: size ? (size.w + 'x' + size.l) : '',
        _fit_rank: String(results.out.items.indexOf(item) + 1),
        _fit_index: (idx.match_version || '') + ':' + (window.__FIT_SHA || ''),
        _fit_shown: results.out.items.map(function (i) {
          return i.product.sku;
        }).join(',')
      }
    };
    cartAttrs(attrs.attributes);
  }

  /* ONE writer for both callers. Two copies of "post an attribute" is the
   * second-implementation fault this repo has paid for three times. */
  var cartQ = Promise.resolve();
  function cartAttrs(attributes) {
    /* SERIALISED. /cart/update.js MUTATES the cart, and two unordered writes
     * can settle on the older snapshot. Chaining costs nothing and makes the
     * ordering a property rather than a hope. */
    cartQ = cartQ.then(function () { return postCart(attributes); })
                 .catch(function () { /* never let one failure stop the chain */ });
  }
  function postCart(attributes) {
    try {
      /* keepalive, because recordChoice fires on a click that NAVIGATES - the
       * browser cancels an ordinary in-flight request as the page unloads, so
       * without this an unknown share of choice records never leave at all. */
      return fetch('/cart/update.js', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () {
        /* fetch REJECTS, it does not throw - the bare try/catch this replaced
         * never caught a network failure, it only looked as though it did. */
      });
    } catch (e) {
      return Promise.resolve();      /* never block a click on telemetry */
    }
  }

  /* ── boot ────────────────────────────────────────────────────────────── */
  /* ONE PATH FROM A FILE TO A RESULT, so a photo held while the catalogue
   * loaded and a photo chosen after it are not two code paths that can drift.
   * `pending` is the file waiting for the index, and only `init` sets it. */
  var pending = null;

  function startPhoto(f) {
    /* A NEW PHOTO CLOSES THE FAILURE DIALOG. It was opened to explain the LAST
     * one, and leaving it up over a working spinner reads as the new photo
     * having failed too. */
    var dlg = $('[data-rf-dialog]');
    if (dlg && dlg.open) { try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); } }
    /* THE UPLOAD SCREEN IS WHERE THE WORKING STATE BELONGS, so somebody who
     * retried from the dialog watches it there rather than on whatever screen
     * happened to be behind it. Advancing to screen 4 happens on SUCCESS - a
     * failure then has somewhere to return to that still has the box on it. */
    if (screen > 3) go(3, true);
    phase('opening');
    return loadPhoto(f).then(function (p) {
      photo = p;
      return run(p);
    }).catch(function (e) {
      show('problem', { message: String(e && e.message || e) });
    });
  }

  function init() {
    root = $('[data-rug-finder]');
    if (!root) return;
    STEPS = readSteps();
    bindFlow();
    bindSwipe();
    bindReviews();

    /* THE CAMERA CONTROL WAS DELETED ON 2026-09-07, AND SO WAS THE CODE THAT
     * REVEALED IT. There were two inputs: a drop box and a second one carrying
     * `capture="environment"`, shown only on a coarse pointer because `capture`
     * is IGNORED on desktop Chrome, where it would have opened the same file
     * picker under a different label.
     *
     * By direct decision of the user there is now ONE control, and it offers
     * both routes: a plain `accept="image/*"` input opens the platform's own
     * sheet - Take Photo, Photo Library, Choose File. The second control only
     * ever existed to FORCE the camera, and forcing it removed the choice a
     * customer photographing a room they are not standing in actually needs. */

    loadIndex().then(function (i) {
      idx = i;
      root.classList.add('is-ready');
      /* A PHOTO CHOSEN WHILE THE CATALOGUE WAS STILL LOADING IS RUN NOW,
       * RATHER THAN LEFT AS A DEAD END (2026-09-06).
       *
       * FOUND BY DRIVING THE PAGE, not by reading it: an upload one second
       * after load printed "Still loading the catalogue - try again in a
       * moment" and then did NOTHING, ever. The file stays in the input, so
       * re-picking the SAME file fires no `change` event at all - the
       * customer has to choose a different photo, or reload. On a page whose
       * entire job is getting one photograph uploaded, that is the worst
       * possible place to have a dead end.
       *
       * The index is 48.7 KB gzipped from R2, so the window is short - but it
       * is exactly the window a fast picker lands in, and the fold is built
       * to make people pick fast. */
      if (pending) {
        var f = pending;
        pending = null;
        startPhoto(f);
      }
    }).catch(function (e) {
      show('problem', { message: 'The rug finder could not load its catalogue. ' +
                                 'You can still browse the collection.',
                        detail: String(e && e.message || e) });
    });

    /* EVERY FILE INPUT ON THE PAGE, ONE HANDLER. The drop box, the camera
     * control and the dialog's retry are three ways of choosing a photograph
     * and must not be three code paths - which is why they share the
     * `data-fit-file` attribute rather than three ids. The dialog lives outside
     * `root`, so this queries the DOCUMENT. */
    var inputs = document.querySelectorAll('[data-fit-file]');
    for (var ii = 0; ii < inputs.length; ii++) bindInput(inputs[ii]);

    function bindInput(input) {
    input.addEventListener('change', function () {
      var f = input.files && input.files[0];
      if (!f) return;
      /* THE VALUE IS CLEARED SO THE SAME FILE CAN BE CHOSEN TWICE. Without it
       * a customer who picks the same photo again fires no `change` event at
       * all - the dead end this file already records once, arriving through a
       * second door now that there are three inputs and a retry. */
      setTimeout(function () { try { input.value = ''; } catch (e) {} }, 0);
      if (!idx) {
        /* HELD, NOT REFUSED. The message says what is happening rather than
         * asking the customer to do something again - and `pending` is what
         * makes that true, because the index handler above runs it the moment
         * the catalogue lands. */
        pending = f;
        phase('opening');
        return;
      }
      startPhoto(f);
    });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
}());
